import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLanguage } from '../../contexts/LanguageContext';
import { BackLink, Button, Container, Header, Message, Subtitle, Title } from './ui';
import ConfirmDialog from './ConfirmDialog';
import EditorsArea from './EditorsArea';
import ImportsArea from './ImportsArea';
import OrganizersArea from './OrganizersArea';
import PeopleArea from './PeopleArea';
import PublishArea from './PublishArea';
import ProgramArea from './ProgramArea';
import EventBar, { AreaTabs } from './ProgramAdminHeader';
import {
  DISPLAY_KINDS, DISPLAY_KIND_NAMES, HIGHLIGHTED_BOX, countSpotFilters, createOf, dayLabel, daysOf, declineSuggestionOf,
  elementInRemoved, entryChangeOf, entryPath, indexProgram, invitedInSubtree, isEntry, nodeTitle, notifyOf, peopleRows,
  personById, personReminders, pick, placeSiblings, positionOf, roomName, siblingEntries, subtreeSpots, venueText,
} from './programAdminModel';
import { formatTime } from '../program/programModel';
import { PERMISSIONS, useCan } from '../authority/permissions';

const AREAS = [
  { key: 'program', id: 'programAdmin.area.program', def: 'Program' },
  { key: 'people', id: 'programAdmin.area.people', def: 'People' },
  { key: 'imports', id: 'programAdmin.area.imports', def: 'Imports & PDF' },
  { key: 'organizers', id: 'programAdmin.area.organizers', def: 'Organizers & committee' },
  { key: 'publish', id: 'programAdmin.area.publish', def: 'Publish' },
  { key: 'editors', id: 'programAdmin.area.editors', def: 'Editors' },
];

const ROLE_VALUES = ['SPEAKER', 'MODERATOR', 'PANELIST'];
const ENTRY_KINDS = ['OPENING', 'SESSION', 'PANEL', 'KEYNOTE', 'WORKSHOP', 'SATELLITE', 'BREAK', 'ARRIVAL',
  'INTERLUDE', 'SOCIAL', 'MATCH', 'CLOSING', 'TALK', 'TOPIC', 'DISCUSSION'];
const VISIBILITIES = ['PUBLIC', 'INTERNAL'];

// The preview's per-person rendering, merged back into the tell step's rows (the rows also carry the
// person's choice and the changes, which the preview does not repeat).
const mergeRendered = (rows, preview) => {
  const byPerson = new Map((preview?.emails || []).map((mail) => [mail.personId, mail]));
  return (rows || []).map((row) => {
    const mail = byPerson.get(row.personId);
    return mail ? { ...row, subject: mail.subject, renderedHtml: mail.html } : row;
  });
};

// The one shared edit, as the change route takes it: only what the admin changed, so an untouched
// email stays the saved/default template (null).
const sharedTemplateOf = (shared) => {
  if (!shared) return null;
  const changed = {};
  if (shared.subject !== shared.baselineSubject) changed.subject = shared.subject;
  if (shared.html !== shared.baselineHtml) changed.html = shared.html;
  return Object.keys(changed).length > 0 ? { ...changed, highlight: true } : null;
};

const fieldsOf = (fields) => Object.fromEntries((fields || []).map((f) => [f.key, f.value]));

const DELIVERY_TEXT = {
  SENT: 'email sent',
  FAILED: 'sending failed',
  NO_EMAIL: 'no email address',
  NO_CHANGE: 'nothing changed for them',
};

// The reason tag of a time-shift row (TimeShifts: PUSHED, CARRIED, EXTENDED, PULLED), in the admin's
// language.
const REASON_TEXT = {
  PUSHED: 'moves later',
  CARRIED: 'moves with its block',
  EXTENDED: 'ends later',
  PULLED: 'moves earlier',
};

// "HH:mm" to minutes of the day, for the gap's length (never for parsing a time zone).
const minutesOfClock = (hhmm) => {
  const [hours, minutes] = String(hhmm || '').split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

// A warning of the preview's `overlaps`, as the one key the second preview is compared by.
const overlapKeyOf = (warning) => [
  warning?.code, warning?.entryId, warning?.otherEntryId ?? '', warning?.personId ?? '',
].join('|');

// Whether the preview shows anything the time cascade has to ask about: points that would move, a
// gap that could be closed, overlaps, or a shift the backend refused.
const hasTimesPictures = (preview) => !!preview?.shiftRefusal
  || (preview?.shifts || []).length > 0
  || (preview?.gapClosing || []).length > 0
  || (preview?.overlaps || []).length > 0;

// What went wrong, as the backend says it: the admin API answers a plain-text body (409 for a rule,
// 502 when a mail failed), so that text is the message the dialog shows.
const errorText = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data && typeof data.message === 'string' && data.message) return data.message;
  if (err?.message) return err.message;
  return fallback;
};

// The day after a date-only value, in the same "YYYY-MM-DD" form (UTC keeps it away from a time zone).
const dateAfter = (iso) => {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

// What "Add day" opens with: the day after the last existing day, else the date of the event's
// date-time, else nothing.
const defaultDayDate = (program, days) => {
  const last = (days || [])[days.length - 1];
  if (last?.day) return dateAfter(last.day);
  const eventDateTime = program?.event?.eventDateTime;
  return eventDateTime ? String(eventDateTime).slice(0, 10) : '';
};

// The venue a new room starts on: the venue the event's rooms share, else the event's default venue,
// else the only venue, else none.
const venuePreselectOf = (program, venues) => {
  const used = [...new Set((program?.rooms || []).map((room) => room.venueId))];
  if (used.length === 1) return used[0];
  if (program?.event?.defaultVenueId != null) return program.event.defaultVenueId;
  if ((venues || []).length === 1) return venues[0].id;
  return null;
};

// The admin Program screen (P11, plan §7.7). The view state lives here - which event, which area,
// which day, what is selected and ticked, which filters are on, which confirmation is open - and so
// does the description of every action: what the backend will do, who gets an email and whether the
// history is kept. Everything that changes data goes out through `previewEmails` (the backend's own
// email preview) and `runAction` (the write itself), both owned by ProgramAdminContainer; after a
// successful write it reloads the program and the people from the server.
//
// The presentational parts (`program`, `people`, `events`) and the two runners can also be handed in
// directly, which is how the fixture-driven tests exercise the action rules without a backend.
const ProgramAdminScreen = ({
  events = [], program, people = [], initialEventId, initialArea = 'program', initialSpotId,
  language: languageProp,
  eventId: eventIdProp, onSelectEvent,
  loading = false, error: loadError = null, onRetry,
  previewEmails, refreshPreview, previewChange, runAction, searchPeople, createPerson, findPersonMatches, fetchSuggestion,
  fetchVenues, onPreviewPublic, onReload, onDownloadMaterials,
}) => {
  const intl = useIntl();
  const can = useCan();
  const { language: siteLanguage } = useLanguage();
  // Titles and labels follow the site language; `language` lets a caller pin it (the tests, and the
  // same escape hatch the public section view has).
  const language = languageProp || siteLanguage;
  // Memoized so the action callbacks below keep stable dependencies (react-hooks/exhaustive-deps).
  const t = useCallback((id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values), [intl]);

  const [internalEventId, setInternalEventId] = useState(initialEventId ?? events[0]?.id ?? null);
  const eventId = eventIdProp ?? internalEventId;
  const [area, setArea] = useState(initialArea);
  const [dayId, setDayId] = useState(null);
  const [selection, setSelection] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [spotFilter, setSpotFilter] = useState(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [status, setStatus] = useState(null);
  const confirmRef = useRef(null);
  confirmRef.current = confirm;
  const tellSeq = useRef(0);

  const days = useMemo(() => daysOf(program), [program]);
  const index = useMemo(() => indexProgram(program), [program]);
  const filterCounts = useMemo(() => countSpotFilters(program, people), [program, people]);
  const rows = useMemo(() => peopleRows(program, people), [program, people]);
  const currentDayId = days.some((d) => d.id === dayId) ? dayId : days[0]?.id ?? null;

  const activeSelection = useMemo(() => {
    if (!selection) return null;
    if (selection.kind === 'person') return { kind: 'person', id: selection.id };
    if (selection.kind === 'spot') {
      const found = index.spots.get(selection.id);
      return found ? { kind: 'spot', id: selection.id, assignment: found.assignment, entry: found.entry } : null;
    }
    if (selection.kind === 'element') {
      const found = index.elements.get(selection.id);
      return found ? {
        kind: 'element', id: selection.id, element: found.element, parent: found.parent, removed: elementInRemoved(index, selection.id),
      } : null;
    }
    const found = index.entries.get(selection.id);
    return found ? { kind: 'entry', id: selection.id, entry: found.entry } : null;
  }, [selection, index]);

  // Where the selected entry sits among its siblings: the first has nothing above it, the last
  // nothing below it (ProgramService.move swaps two siblings).
  const entryPosition = useMemo(() => {
    if (activeSelection?.kind !== 'entry') return null;
    const { entry } = activeSelection;
    return positionOf(siblingEntries(program, entry, index.entries.get(entry.id)?.parent), entry);
  }, [activeSelection, program, index]);

  // The deep link (?event=…&spot=…) opens the spot's panel and jumps to its day, once.
  const deepLinked = useRef(false);
  useEffect(() => {
    if (deepLinked.current || initialSpotId == null || !program) return;
    const found = index.spots.get(initialSpotId);
    if (!found) return;
    deepLinked.current = true;
    setSelection({ kind: 'spot', id: initialSpotId });
    if (found.entry.eventDayId != null) setDayId(found.entry.eventDayId);
  }, [initialSpotId, program, index]);

  // The panel is next to the list, so selecting something scrolls it into view.
  useEffect(() => {
    if (activeSelection?.kind !== 'spot') return;
    const el = typeof document !== 'undefined' && document.querySelector(`[data-assignment-id="${activeSelection.id}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
  }, [activeSelection]);

  // ---------------------------------------------------------------- the words the screen re-uses

  const roleText = useCallback((role) => t(`programAdmin.role.${role}`, role), [t]);
  const kindText = useCallback((kind) => t(`programAdmin.kind.${kind}`, kind), [t]);
  const spotLabelOf = useCallback((assignmentId) => {
    const found = index.spots.get(assignmentId);
    return found ? `${nodeTitle(found.entry, language)} (${roleText(found.assignment.role)})` : `${assignmentId}`;
  }, [index, language, roleText]);
  const labelsOf = useCallback((ids) => (ids || []).map(spotLabelOf), [spotLabelOf]);

  // Every spot the person is waiting on (an open invitation): what a reminder and an invitation name.
  const openSpotsOf = useCallback((personId) => [...index.spots.values()]
    .filter(({ assignment }) => !assignment.removedAt && assignment.personId === personId && assignment.status === 'INVITED')
    .map(({ assignment }) => assignment.id), [index]);

  // ---------------------------------------------------------------- the dialog

  // A dialog opens with its description, then the backend's email preview fills the email part in.
  // A dialog that describes what the backend plans (`load`, an async function answering a patch of
  // the dialog) waits for that answer with its confirm button disabled. `token` keeps a slow preview
  // or plan from landing in a dialog that was closed or replaced meanwhile.
  const dialogSeq = useRef(0);
  const openDialog = useCallback((config) => {
    const token = ++dialogSeq.current;
    const { load, ...rest } = config;
    const target = config.target || null;
    setStatus(null);
    setConfirm({
      tone: 'default', points: [], note: null, fields: [], emails: [], history: null, confirmLabel: null,
      error: null, busy: false, target, loading: !!load, stage: null, change: null, shared: null, secondaryAction: null,
      timeRows: [], timeRowsTitle: null, warnings: [], warningsTitle: null, timesPreview: null, timesRefusal: null,
      shownOverlaps: [],
      showCancel: true, ...rest,
    });
    if (load) {
      Promise.resolve()
        .then(load)
        .then((patch) => {
          if (dialogSeq.current !== token) return;
          setConfirm((c) => (c ? { ...c, ...patch, loading: false } : c));
        })
        .catch((err) => {
          if (dialogSeq.current !== token) return;
          setConfirm((c) => (c ? {
            ...c, loading: false, confirmDisabled: true,
            error: errorText(err, t('programAdmin.confirm.loadFailed', 'Could not load this from the server.')),
          } : c));
        });
    }
    if (target && previewEmails) {
      Promise.resolve()
        .then(() => previewEmails(target))
        .then((mails) => {
          if (dialogSeq.current !== token) return;
          setConfirm((c) => (c ? { ...c, emails: (mails || []).map((mail, i) => {
            const owner = (target.people || []).find((p) => p.personId === mail.personId);
            const hasEmail = mail.hasEmail !== false && !!mail.to;
            // An invitation or reminder previews with its editable {{placeholder}} template
            // (RenderedMail.template): the dialog edits that, not the finished email, and only the
            // edited part goes back with the write. A notice has none and edits its plain text.
            const template = mail.template
              ? { subject: mail.template.subject || '', html: mail.template.html || '',
                  originalSubject: mail.template.subject || '', originalHtml: mail.template.html || '' }
              : null;
            return {
              key: mail.personId ?? `mail-${i}`,
              personId: mail.personId ?? owner?.personId ?? null,
              name: mail.name || owner?.name || '',
              to: mail.to || '',
              hasEmail,
              send: hasEmail,
              spots: mail.assignmentIds && mail.assignmentIds.length
                ? labelsOf(mail.assignmentIds) : (owner?.labels || []),
              subject: mail.subject || '',
              html: mail.html || '',
              previewHtml: mail.previewHtml || undefined,
              template,
              placeholders: mail.placeholders || [],
              templateProblems: mail.templateProblems || [],
              renderedHtml: mail.html || '',
              assignmentIds: owner?.assignmentIds || mail.assignmentIds || [],
            };
          }) } : c));
        })
        .catch((err) => {
          if (dialogSeq.current !== token) return;
          setConfirm((c) => (c ? {
            ...c,
            error: errorText(err, t('programAdmin.confirm.previewFailed', 'Could not load the email preview from the server.')),
          } : c));
        });
    }
  }, [labelsOf, previewEmails, t]);

  const closeDialog = useCallback(() => {
    dialogSeq.current += 1;
    tellSeq.current += 1;
    setConfirm(null);
  }, []);

  // Every field edit goes through here. The room dialog's "New venue…" is the one field that brings
  // others with it: the venue's name (required) and city, shown only while it is chosen.
  const changeField = useCallback((key, value) => setConfirm((c) => {
    if (!c) return c;
    let fields = c.fields.map((f) => (f.key === key ? { ...f, value } : f));
    if (c.target?.kind === 'saveRoom' && key === 'venueId') {
      const hasNewVenue = fields.some((f) => f.key === 'newVenueName');
      if (value === 'NEW' && !hasNewVenue) {
        fields = [...fields,
          { key: 'newVenueName', label: t('programAdmin.rooms.newVenueName', 'Venue name'), value: '', required: true },
          { key: 'newVenueCity', label: t('programAdmin.rooms.newVenueCity', 'City'), value: '' }];
      } else if (value !== 'NEW' && hasNewVenue) {
        fields = fields.filter((f) => f.key !== 'newVenueName' && f.key !== 'newVenueCity');
      }
    }
    return { ...c, fields };
  }), [t]);

  // "Refresh preview": re-render the edited template through the backend's preview route and show
  // that email instead. A refusal (a 400 for an edit that drops the personal link) stays in the
  // dialog; nothing is sent.
  const refreshEmailPreview = useCallback(async (key) => {
    if (!confirm || !refreshPreview) return;
    const email = (confirm.emails || []).find((e) => e.key === key);
    if (!email?.template) return;
    const patch = (changes) => setConfirm((c) => (c ? {
      ...c, emails: c.emails.map((m) => (m.key === key ? { ...m, ...changes } : m)),
    } : c));
    patch({ refreshing: true, refreshError: null });
    try {
      const mail = await refreshPreview(confirm.target, email);
      patch({ refreshing: false, renderedHtml: mail.html, templateProblems: mail.templateProblems || [] });
    } catch (err) {
      patch({ refreshing: false, refreshError: errorText(err, t('programAdmin.confirm.previewFailed', 'Could not load the email preview from the server.')) });
    }
  }, [confirm, refreshPreview, t]);

  // ---------------------------------------------------------------- program changes and notices

  // The people the preview says are affected, one row per person: their name, address, the
  // assignments the choice covers and what changes for them. Only CONFIRMED and INVITED people are
  // in `affected`; a person without an address has no box.
  const affectedRowsOf = useCallback((affected) => {
    const byPerson = new Map();
    for (const a of affected || []) {
      let row = byPerson.get(a.personId);
      if (!row) {
        const person = personById(people, a.personId);
        row = {
          key: a.personId, personId: a.personId, name: person?.programName || String(a.personId),
          to: person?.contactEmail || '', hasEmail: false, send: false,
          changes: [], assignmentIds: [], renderedHtml: '', subject: '',
        };
        byPerson.set(a.personId, row);
      }
      row.hasEmail = row.hasEmail || !!a.hasEmail;
      row.assignmentIds.push(a.assignmentId);
      for (const change of a.changes || []) row.changes.push(change);
    }
    return [...byPerson.values()].map((row) => ({ ...row, send: row.hasEmail }));
  }, [people]);

  // The title of an entry in the screen's language, for the time-cascade list (the row carries no
  // title; it is matched against the loaded program tree, like the list does).
  const entryTitleOf = useCallback((entryId) => {
    const found = index.entries.get(entryId);
    return (found && nodeTitle(found.entry, language))
      || t('programAdmin.confirm.times.unknownEntry', 'Entry {id}', { id: entryId });
  }, [index, language, t]);

  // The preview's shift rows as the dialog prints them: title, old -> new time range (EXTENDED only
  // shows its end) and the reason tag. A CARRIED row is indented when one of its ancestors is in the
  // same list; rows keep the backend's order.
  const shiftRowsOf = useCallback((shifts) => {
    const listed = new Set((shifts || []).map((row) => row.entryId));
    return (shifts || []).map((row) => {
      let indent = false;
      if (row.reason === 'CARRIED') {
        let node = index.entries.get(row.entryId)?.parent;
        while (node && !indent) {
          indent = listed.has(node.id);
          node = index.entries.get(node.id)?.parent;
        }
      }
      return {
        key: `${row.reason}-${row.entryId}-${row.fromStart ?? ''}`,
        indent,
        title: entryTitleOf(row.entryId),
        times: row.reason === 'EXTENDED'
          ? t('programAdmin.confirm.times.ends', 'ends {from} -> {to}', { from: row.fromEnd, to: row.toEnd })
          : `${row.fromStart}-${row.fromEnd} -> ${row.toStart}-${row.toEnd}`,
        tag: t(`programAdmin.confirm.times.reason.${row.reason}`, REASON_TEXT[row.reason] || row.reason),
      };
    });
  }, [entryTitleOf, index, t]);

  // The preview's new warnings as one sentence each, built the way the brief words them; the person
  // is the one the screen already shows, falling back to the backend's own message.
  const overlapLinesOf = useCallback((warnings) => (warnings || []).map((warning) => {
    const a = entryTitleOf(warning.entryId);
    const b = entryTitleOf(warning.otherEntryId);
    switch (warning.code) {
      case 'OVERLAP_IN_ROOM':
        return t('programAdmin.confirm.times.overlap.OVERLAP_IN_ROOM', '{a} overlaps {b} in the same room.', { a, b });
      case 'CHILD_OVERRUN':
        return t('programAdmin.confirm.times.overlap.CHILD_OVERRUN', '{a} ends after {b}, the block it is part of.', { a, b });
      case 'CHILD_BEFORE_PARENT':
        return t('programAdmin.confirm.times.overlap.CHILD_BEFORE_PARENT', '{a} starts before {b}, the block it is part of.', { a, b });
      case 'PERSON_IN_TWO_PLACES': {
        const person = personById(people, warning.personId)?.programName;
        if (person) {
          return t('programAdmin.confirm.times.overlap.PERSON_IN_TWO_PLACES',
            '{person} is in {a} and {b} at the same time.', { person, a, b });
        }
        return warning.message || t('programAdmin.confirm.times.overlap.PERSON_IN_TWO_PLACES',
          '{person} is in {a} and {b} at the same time.', { person: warning.personId, a, b });
      }
      default:
        return warning.message || '';
    }
  }).filter(Boolean), [entryTitleOf, people, t]);

  // After a save that told people: one line per person with the delivery the backend reported. A
  // removal answers its cancellation outcomes in `notices` and the change mails in `changeNotices`;
  // an edit or a create answers the change mails in `notices` and has no `changeNotices`.
  const showResult = useCallback((result) => {
    const notices = [...(result?.notices || []), ...(result?.changeNotices || [])];
    const moved = (result?.shifted || []).length;
    const points = [
      ...(moved > 0 ? [t('programAdmin.confirm.times.shifted',
        '{count, plural, one {# other program point was moved.} other {# other program points were moved.}}',
        { count: moved })] : []),
      ...(notices.length > 0
        ? notices.map((notice) => {
          const person = personById(people, notice.personId);
          return t('programAdmin.confirm.tell.outcome', '{name}: {outcome}', {
            name: person?.programName || String(notice.personId),
            outcome: t(`programAdmin.confirm.tell.delivery.${notice.delivery}`, DELIVERY_TEXT[notice.delivery] || notice.delivery),
          });
        })
        : moved > 0 ? [] : [t('programAdmin.confirm.tell.nobodySaved', 'Saved; nobody was told.')]),
    ];
    setConfirm({
      stage: 'result', tone: 'default',
      title: t('programAdmin.confirm.tell.resultTitle', 'Saved. Who was told:'),
      points, emails: [], fields: [], history: null,
      confirmLabel: t('programAdmin.confirm.tell.close', 'Close'),
      showCancel: false, loading: false, busy: false, error: null, shared: null, secondaryAction: null,
    });
  }, [people, t]);

  // One write of a program change: the change route, with the chosen assignment ids and the one edit
  // (null when untouched). `tell` opens the per-person result afterwards; without it a save that moved
  // other points says so in the status line above the program. An added block switches the screen to
  // its day, as the plain create did.
  const saveChange = useCallback(async (target, change, values, { ids = [], template = null, tell = false } = {}) => {
    setConfirm((c) => (c ? { ...c, busy: true, error: null } : c));
    try {
      const result = await runAction?.(target, {
        fields: values, emails: [], change, notifyAssignmentIds: ids, template,
      });
      if (tell) {
        if (target?.kind === 'addBlock' && change?.create?.eventDayId != null) {
          setDayId(Number(change.create.eventDayId));
        }
        showResult(result);
        return;
      }
      closeDialog();
      if (target?.kind === 'addBlock' && change?.create?.eventDayId != null) {
        setDayId(Number(change.create.eventDayId));
      }
      const moved = (result?.shifted || []).length;
      setStatus(moved > 0 ? t('programAdmin.confirm.times.shifted',
        '{count, plural, one {# other program point was moved.} other {# other program points were moved.}}',
        { count: moved }) : null);
    } catch (err) {
      setConfirm((c) => (c ? { ...c, busy: false, error: errorText(err, t('programAdmin.confirm.failed', 'The action was refused.')) } : c));
    }
  }, [closeDialog, runAction, showResult, t]);

  // The second step when somebody is affected: every affected person with an address preselected,
  // one editable email for all of them, and a preview per chosen person. The first preview had no
  // ids (it only answered who is affected), so the selected ids are previewed again for the emails.
  // `change` is the fixed change, `times` included, that every request and the save carry from here
  // on; `overlaps` are the warnings the times step did not show yet, said in the intro.
  const enterTell = useCallback(async (target, change, first, { overlaps = [] } = {}) => {
    const token = ++tellSeq.current;
    const rows = affectedRowsOf(first.affected);
    const text = first.template || { subject: '', html: '' };
    const shared = {
      templateKey: 'PROGRAM_CHANGE',
      subject: text.subject || '', html: text.html || '',
      baselineSubject: text.subject || '', baselineHtml: text.html || '',
      placeholders: first.placeholders || [],
      problems: first.templateProblems || [],
      previewedKey: JSON.stringify([text.subject || '', text.html || '']),
      error: null,
    };
    const ids = rows.filter((row) => row.send).flatMap((row) => row.assignmentIds);
    setConfirm((c) => (c ? {
      // The change is fixed from here on: the form is gone, so the people, their changes and the
      // emails shown are the ones the save will use. Cancel and open the dialog again to change it.
      ...c, loading: true, busy: false, error: null, stage: 'tell',
      change, fields: [], blockConfirm: null,
      title: t('programAdmin.confirm.tell.title', 'Tell the affected people?'),
      intro: overlaps.length > 0
        ? [t('programAdmin.confirm.times.afterThisChange', 'After this change:'), ...overlapLinesOf(overlaps)].join(' ')
        : null,
      timeRows: [], timeRowsTitle: null, warnings: [], warningsTitle: null,
      points: [
        t('programAdmin.confirm.tell.savedWhenChosen', 'The change is saved when you choose one of the two buttons below.'),
        t('programAdmin.confirm.tell.choose', 'Every affected person with an email address is ticked; untick anyone who should not be told.'),
        t('programAdmin.confirm.tell.oneEmail', 'One email for everyone; the list shows what each chosen person gets. It is not saved.'),
      ],
      emails: rows,
      shared,
      history: null,
      confirmDisabled: false,
      secondaryAction: {
        label: t('programAdmin.confirm.tell.saveWithout', 'Save without telling anyone'),
        onClick: () => {
          const current = confirmRef.current;
          if (!current) return;
          saveChange(current.target, current.change, {}, { ids: [] });
        },
      },
    } : c));
    try {
      const preview = await previewChange?.(target, change, ids);
      if (tellSeq.current !== token) return;
      setConfirm((c) => (c && c.stage === 'tell' ? {
        ...c, loading: false,
        emails: mergeRendered(c.emails, preview),
        shared: { ...c.shared, problems: preview?.templateProblems || [] },
      } : c));
    } catch (err) {
      if (tellSeq.current !== token) return;
      setConfirm((c) => (c ? {
        ...c, loading: false, confirmDisabled: true,
        error: errorText(err, t('programAdmin.confirm.previewFailed', 'Could not load the email preview from the server.')),
      } : c));
    }
  }, [affectedRowsOf, overlapLinesOf, previewChange, saveChange, t]);

  // The last step when nobody is told and the chosen option leaves an overlap the admin has not seen
  // yet: say it once more, with "Save anyway".
  const showOverlapConfirm = useCallback((target, change, overlaps) => {
    setConfirm((c) => (c ? {
      ...c, target, change, stage: 'overlap', loading: false, busy: false, error: null,
      title: t('programAdmin.confirm.times.overlapTitle', 'Save despite this overlap?'),
      intro: null, points: [], note: t('programAdmin.confirm.times.overlapStays', 'The overlap stays; nothing else moves.'),
      fields: [], emails: [], shared: null, history: null, blockConfirm: null,
      timeRows: [], timeRowsTitle: null,
      warnings: overlapLinesOf(overlaps),
      warningsTitle: t('programAdmin.confirm.times.afterThisChange', 'After this change:'),
      confirmLabel: t('programAdmin.confirm.times.saveAnyway', 'Save anyway'),
      secondaryAction: null, showCancel: true, confirmDisabled: false,
    } : c));
  }, [overlapLinesOf, t]);

  // The admin's choice in the times step ("Move them", "Keep the times" or "Continue" with the gap
  // box): it becomes the change's `times`, the preview is asked again with it, and the flow goes on
  // like any other change. A second preview that refuses the shift keeps the dialog open with the
  // backend's sentence. Overlaps this second answer adds are shown before anything is saved.
  const chooseTimes = useCallback(async (times) => {
    const current = confirmRef.current;
    if (!current) return;
    const change = { ...current.change, times };
    const shown = new Set(current.shownOverlaps || []);
    setConfirm((c) => (c ? { ...c, busy: true, error: null } : c));
    try {
      const preview = await previewChange?.(current.target, change, []);
      if (preview?.shiftRefusal) {
        setConfirm((c) => (c ? { ...c, busy: false, error: preview.shiftRefusal } : c));
        return;
      }
      const fresh = (preview?.overlaps || []).filter((warning) => !shown.has(overlapKeyOf(warning)));
      if ((preview?.affected || []).length > 0) {
        await enterTell(current.target, change, preview, { overlaps: fresh });
        return;
      }
      if (fresh.length > 0) {
        showOverlapConfirm(current.target, change, fresh);
        return;
      }
      await saveChange(current.target, change, {}, { ids: [] });
    } catch (err) {
      setConfirm((c) => (c ? {
        ...c, busy: false, error: errorText(err, t('programAdmin.confirm.failed', 'The action was refused.')),
      } : c));
    }
  }, [enterTell, previewChange, saveChange, showOverlapConfirm, t]);

  // The times step, before the tell step: the points that would move (title, old -> new time, reason
  // tag), the gap that can be closed (one unticked box), the new overlaps, or the backend's refusal.
  // "Move them" sends shiftFollowing true, "Keep the times" false; a gap gets one "Continue" button.
  // The form is gone from here on; Cancel saves nothing.
  const showTimes = useCallback((target, change, preview) => {
    const shifts = preview?.shifts || [];
    const gap = preview?.gapClosing || [];
    const overlaps = preview?.overlaps || [];
    const refusal = preview?.shiftRefusal || null;
    const firstPulled = gap.find((row) => row.reason === 'PULLED');
    const gapMinutes = firstPulled
      ? Math.abs(minutesOfClock(firstPulled.fromStart) - minutesOfClock(firstPulled.toStart)) : 0;
    const moving = shifts.length > 0;
    const closing = gap.length > 0;
    setConfirm((c) => (c ? {
      ...c, target, change, stage: 'times', loading: false, busy: false, error: null,
      timesPreview: preview, timesRefusal: refusal,
      shownOverlaps: overlaps.map(overlapKeyOf),
      fields: closing ? [{
        key: 'closeGap', type: 'checkbox', value: false,
        label: t('programAdmin.confirm.times.closeGap',
          'Close the gap: move the following {count, plural, one {# program point} other {# program points}} {minutes} minutes earlier',
          { count: gap.length, minutes: gapMinutes }),
      }] : [],
      points: [], emails: [], shared: null, history: null, blockConfirm: null,
      title: t('programAdmin.confirm.times.title', 'Other program points are affected'),
      intro: refusal || (moving ? t('programAdmin.confirm.times.wouldMove', 'These program points would move:') : null),
      timeRows: moving ? shiftRowsOf(shifts) : closing ? shiftRowsOf(gap) : [],
      timeRowsTitle: null,
      warnings: overlapLinesOf(overlaps),
      warningsTitle: t('programAdmin.confirm.times.afterThisChange', 'After this change:'),
      note: refusal
        ? t('programAdmin.confirm.times.refusalHint', 'Choose "Keep the times" or change the time.')
        : moving
          ? t('programAdmin.confirm.times.keepThemNote', 'With "Keep the times", the overlap stays and is shown as a warning.')
          : null,
      confirmLabel: refusal ? null
        : moving ? t('programAdmin.confirm.times.moveThem', 'Move them')
          : t('programAdmin.confirm.times.continue', 'Continue'),
      secondaryAction: (refusal || moving) ? {
        label: t('programAdmin.confirm.times.keepThem', 'Keep the times'),
        onClick: () => chooseTimes({ shiftFollowing: false, closeGap: false }),
      } : null,
      showCancel: true, confirmDisabled: false,
    } : c));
  }, [chooseTimes, overlapLinesOf, shiftRowsOf, t]);

  // Ticking a person in the tell step re-previews: the emails are per chosen person, and one email
  // may list several of a person's entries.
  const toggleSendEmail = useCallback((key, send) => {
    const current = confirmRef.current;
    if (!current) return;
    if (current.stage !== 'tell') {
      setConfirm((c) => (c ? { ...c, emails: c.emails.map((m) => (m.key === key ? { ...m, send } : m)) } : c));
      return;
    }
    const rows = (current.emails || []).map((m) => (m.key === key ? { ...m, send } : m));
    setConfirm((c) => (c ? { ...c, emails: rows, error: null } : c));
    const token = ++tellSeq.current;
    const ids = rows.filter((m) => m.send).flatMap((m) => m.assignmentIds);
    previewChange(current.target, current.change, ids, sharedTemplateOf(current.shared))
      .then((preview) => {
        if (tellSeq.current !== token) return;
        setConfirm((c) => (c && c.stage === 'tell' ? {
          ...c, emails: mergeRendered(rows, preview),
          shared: { ...c.shared, problems: preview?.templateProblems || [] },
        } : c));
      })
      .catch((err) => {
        if (tellSeq.current !== token) return;
        setConfirm((c) => (c ? {
          ...c, error: errorText(err, t('programAdmin.confirm.previewFailed', 'Could not load the email preview from the server.')),
        } : c));
      });
  }, [previewChange, t]);

  // The shared edit is re-rendered for every chosen person after a pause in typing, never per
  // keystroke (the same 500 ms as the template editor). Until that answer is in, the button is off.
  const sharedKey = confirm?.stage === 'tell' && confirm.shared
    ? JSON.stringify([confirm.shared.subject, confirm.shared.html]) : null;
  useEffect(() => {
    if (!sharedKey || !confirm?.shared || confirm.shared.previewedKey === sharedKey) return undefined;
    const timer = setTimeout(async () => {
      const current = confirmRef.current;
      if (!current || current.stage !== 'tell') return;
      const ids = (current.emails || []).filter((e) => e.send).flatMap((e) => e.assignmentIds);
      try {
        const preview = await previewChange?.(current.target, current.change, ids,
          sharedTemplateOf(current.shared));
        setConfirm((c) => (c && c.stage === 'tell'
          && JSON.stringify([c.shared.subject, c.shared.html]) === sharedKey ? {
            ...c,
            emails: mergeRendered(c.emails, preview),
            shared: { ...c.shared, problems: preview?.templateProblems || [], previewedKey: sharedKey, error: null },
          } : c));
      } catch (err) {
        setConfirm((c) => (c && c.stage === 'tell'
          && JSON.stringify([c.shared.subject, c.shared.html]) === sharedKey ? {
            ...c,
            shared: { ...c.shared, error: errorText(err, t('programAdmin.confirm.previewFailed', 'Could not load the email preview from the server.')) },
          } : c));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [sharedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Confirming runs the action and, on a refusal (409 and friends), keeps the dialog open with the
  // backend's own message. On success the owner reloads the program, so no local state drifts, and a
  // dialog with `next` (a function of the confirmed field values) opens the dialog that follows.
  //
  // Every entry change the dialog starts - Edit, a move, Add block, Add child - goes through the change
  // routes: first the preview with nobody chosen and the times option that would move the following
  // points (one extra request) - if the answer shows points that would move, a closable gap,
  // overlaps or a refused shift, the same dialog asks in the times step first; if nobody is
  // affected, the change is saved with no notices and the dialog closes; if somebody is, the dialog
  // switches to the tell step, where the buttons save it with or without the notices. Cancel saves
  // nothing. The change kind only picks the route and the body; the steps after the first preview
  // are the same ones.
  //
  // Remove is today's call while the gap box is unticked. With it ticked, the fixed change carries
  // the cancellation choice made in the first stage and `times.closeGap`, and the tell step saves
  // both the cancellation and the change mails.
  const confirmAction = useCallback(async () => {
    if (!confirm) return;
    if (confirm.stage === 'result') { closeDialog(); return; }
    const { target, fields, emails, stage } = confirm;
    const values = fieldsOf(fields);

    // Day and room writes need no preview: one call, then the container's reload. A created day is
    // selected; a removed one leaves no selection. A refused write (409) stays in the dialog.
    if (['createDay', 'updateDay', 'deleteDay', 'saveRoom', 'deleteRoom'].includes(target?.kind)) {
      setConfirm((c) => (c ? { ...c, busy: true, error: null } : c));
      try {
        const result = await runAction?.(target, { fields: values });
        closeDialog();
        if (target.kind === 'createDay' && result?.id != null) setDayId(Number(result.id));
        if (target.kind === 'deleteDay' && dayId === target.dayId) setDayId(null);
      } catch (err) {
        setConfirm((c) => (c ? {
          ...c, busy: false,
          error: errorText(err, t('programAdmin.confirm.failed', 'The action was refused.')),
        } : c));
      }
      return;
    }

    if (stage === 'tell') {
      const ids = (emails || []).filter((e) => e.send).flatMap((e) => e.assignmentIds);
      if (ids.length === 0) return;
      await saveChange(target, confirm.change, values, { ids, template: sharedTemplateOf(confirm.shared), tell: true });
      return;
    }
    if (stage === 'overlap') {
      await saveChange(target, confirm.change, {}, { ids: [] });
      return;
    }

    if (target?.kind === 'editEntry' || target?.kind === 'moveEntry'
      || target?.kind === 'addChild' || target?.kind === 'addBlock') {
      if (stage === 'times') {
        if (confirm.timesRefusal) return; // only "Keep the times" goes on from a refusal
        const closing = (confirm.timesPreview?.gapClosing || []).length > 0;
        if (!closing && (confirm.timesPreview?.shifts || []).length === 0) {
          // Only overlaps were asked about: nothing to choose, the preview as sent still answers it.
          if ((confirm.timesPreview?.affected || []).length > 0) await enterTell(target, confirm.change, confirm.timesPreview);
          else await saveChange(target, confirm.change, {}, { ids: [] });
          return;
        }
        if (closing) {
          const closeGap = !!(confirm.fields || []).find((f) => f.key === 'closeGap')?.value;
          await chooseTimes({ shiftFollowing: false, closeGap });
        } else {
          await chooseTimes({ shiftFollowing: true, closeGap: false });
        }
        return;
      }
      // The fixed change: an entry change or a create, with the times option that moves the
      // following points. A create never closes a gap (the backend answers gapClosing empty).
      const change = target?.kind === 'addChild' || target?.kind === 'addBlock'
        ? { create: createOf(target, values), times: { shiftFollowing: true, closeGap: false } }
        : { ...entryChangeOf(target, values), times: { shiftFollowing: true, closeGap: false } };
      setConfirm((c) => (c ? { ...c, busy: true, error: null } : c));
      try {
        const preview = await previewChange?.(target, change, []);
        if (hasTimesPictures(preview)) showTimes(target, change, preview);
        else if ((preview?.affected || []).length > 0) await enterTell(target, change, preview);
        else await saveChange(target, change, values, { ids: [] });
      } catch (err) {
        setConfirm((c) => (c ? { ...c, busy: false, error: errorText(err, t('programAdmin.confirm.failed', 'The action was refused.')) } : c));
      }
      return;
    }

    if (target?.kind === 'removeEntry') {
      const closeGap = !!(fields || []).find((f) => f.key === 'closeGap')?.value;
      if (!closeGap) {
        // Exactly today's removal: only the cancellation choice, no times.
        setConfirm((c) => (c ? { ...c, busy: true, error: null } : c));
        try {
          await runAction?.(target, {
            fields: values,
            emails: (emails || []).map((e) => ({
              personId: e.personId, subject: e.subject, html: e.html, template: e.template, send: !!e.send,
            })),
          });
          closeDialog();
        } catch (err) {
          setConfirm((c) => (c ? { ...c, busy: false, error: errorText(err, t('programAdmin.confirm.failed', 'The action was refused.')) } : c));
        }
        return;
      }
      // Closing the gap: the notification choice made above is part of the fixed change, so the
      // tell step cannot replace it with its own rows.
      const change = { notify: notifyOf(emails), times: { closeGap: true } };
      setConfirm((c) => (c ? { ...c, busy: true, error: null } : c));
      try {
        const preview = await previewChange?.(target, change, []);
        if (preview?.shiftRefusal) {
          setConfirm((c) => (c ? { ...c, busy: false, error: preview.shiftRefusal } : c));
          return;
        }
        const overlaps = preview?.overlaps || [];
        if ((preview?.affected || []).length > 0) {
          await enterTell(target, change, preview, { overlaps });
          return;
        }
        if (overlaps.length > 0) {
          showOverlapConfirm(target, change, overlaps);
          return;
        }
        await saveChange(target, change, {}, { ids: [] });
      } catch (err) {
        setConfirm((c) => (c ? { ...c, busy: false, error: errorText(err, t('programAdmin.confirm.failed', 'The action was refused.')) } : c));
      }
      return;
    }

    setConfirm((c) => (c ? { ...c, busy: true, error: null } : c));
    try {
      await runAction?.(target, {
        fields: values,
        emails: (emails || []).map((e) => ({
          personId: e.personId, subject: e.subject, html: e.html, template: e.template, send: !!e.send,
        })),
      });
      closeDialog();
      // A person put on a spot (picked, or just created in the dialog) opens in the side panel, where
      // their research and the changes it proposed wait.
      if (target?.kind === 'replacePerson' && values.personId) setSelection({ kind: 'person', id: Number(values.personId) });
      // A step that leads into another dialog (the address an invitation needs, then the invitation).
      confirm.next?.(values);
    } catch (err) {
      setConfirm((c) => (c ? { ...c, busy: false, error: errorText(err, t('programAdmin.confirm.failed', 'The action was refused.')) } : c));
    }
  }, [chooseTimes, closeDialog, confirm, dayId, enterTell, previewChange, runAction, saveChange, showOverlapConfirm, showTimes, t]);

  const keepHistory = useMemo(() => t('programAdmin.confirm.historyKept', 'The history of who was asked is kept - also for later replacements.'), [t]);
  const dropHistory = useMemo(() => t('programAdmin.confirm.historyDeleted', 'Nobody was ever asked about this spot or entry, so there is no history to keep.'), [t]);
  const noMail = useMemo(() => t('programAdmin.confirm.noMail', 'No email is sent by this action.'), [t]);
  const notifyBullet = useMemo(() => t('programAdmin.confirm.notifyBullet',
    'If confirmed or invited people are affected, you choose next who is told.'), [t]);
  const orderNote = useMemo(() => ({ kept: true, note: t('programAdmin.confirm.order.note', 'Order only - nothing is sent and nothing is removed.') }), [t]);

  const roleOptions = useMemo(() => ROLE_VALUES.map((role) => ({ value: role, label: roleText(role) })), [roleText]);
  const kindOptions = useMemo(() => ENTRY_KINDS.map((kind) => ({ value: kind, label: kindText(kind) })), [kindText]);
  const visibilityOptions = useMemo(() => VISIBILITIES.map((v) => ({ value: v, label: t(`programAdmin.visibility.${v}`, v === 'INTERNAL' ? 'Internal only' : 'Public') })), [t]);
  const roomOptionsOf = useCallback(() => [{ value: '', label: t('programAdmin.edit.noRoom', 'No room') },
    ...(program?.rooms || []).map((room) => ({ value: String(room.id), label: roomName(language, room) }))], [program, t, language]);

  // ---------------------------------------------------------------- the suggested replacement

  // The dialog part that SuggestedPersonService's plan decides: who the suggestion may be and what
  // the admin can choose. A certain match by email is the only choice (a second person with that
  // address cannot be created); a certain match by name is offered first, a new person second (two
  // people can share a name); candidates are listed for the admin to pick, a new person last;
  // nobody similar leaves creating. `choice` (a person id, or 'create') goes back with the write.
  const suggestionDialogOf = useCallback((plan, spot, declined) => {
    const personLine = (m) => [m.programName, m.institution, m.contactEmail].filter(Boolean).join(' · ');
    const detailOf = (m) => [
      t(`programAdmin.match.reason.${m.reason}`, m.reason),
      m.hasAccount ? t('programAdmin.match.hasAccount', 'has an account') : t('programAdmin.match.noAccount', 'no account yet'),
      m.refusal,
    ].filter(Boolean).join(' · ');
    const name = plan.suggestedName || plan.suggestedEmail;
    const createOption = plan.create ? [{
      value: 'create',
      label: t('programAdmin.confirm.suggestion.createOption', 'Create a new person {name}', { name }),
      detail: plan.create.contactEmail
        ? t('programAdmin.confirm.suggestion.created', 'A new person with the address {email}', { email: plan.create.contactEmail })
        : t('programAdmin.confirm.suggestion.createdNoEmail', 'A new person without an email address (add one before inviting)'),
      disabled: !plan.create.lastName,
    }] : [];
    const pick = (options, value) => [{
      key: 'choice', type: 'choice', required: true, value,
      label: t('programAdmin.confirm.suggestion.pick', 'Who takes the spot (not invited yet)?'), options,
    }];
    const target = { kind: 'useSuggestion', assignmentId: spot.id, invitationId: plan.invitationId };
    const match = plan.match;
    if (match) {
      const title = t('programAdmin.confirm.suggestion.exists', '{name} already exists – put them on this spot?', { name: match.programName });
      if (!plan.create) {
        return {
          title, target: { ...target, choice: String(match.personId) }, error: match.refusal || null,
          note: t('programAdmin.confirm.suggestion.noteMatched', '{name} ({email}) takes the spot and is not invited yet. {declined} comes off it; no email is sent.', { name: match.programName, email: match.contactEmail || plan.suggestedEmail, declined }),
        };
      }
      return {
        title, target,
        note: t('programAdmin.confirm.suggestion.note', '{declined} comes off the spot. The person you choose is not invited yet; no email is sent.', { declined }),
        fields: pick([{ value: String(match.personId), label: personLine(match), detail: detailOf(match), disabled: !!match.refusal }, ...createOption],
          match.refusal ? '' : String(match.personId)),
      };
    }
    if ((plan.candidates || []).length > 0) {
      return {
        target,
        note: t('programAdmin.confirm.suggestion.noteSimilar', 'Similar people already exist: pick the one meant, or create a new person. {declined} comes off the spot; no email is sent.', { declined }),
        fields: pick([...plan.candidates.map((c) => ({
          value: String(c.personId), label: personLine(c), detail: detailOf(c), disabled: !!c.refusal,
        })), ...createOption], ''),
      };
    }
    return {
      target: { ...target, choice: 'create' }, error: plan.refusal || null,
      note: plan.create?.contactEmail
        ? t('programAdmin.confirm.suggestion.noteCreate', 'A new person {name} ({email}) is created and takes the spot, not invited yet. {declined} comes off it; no email is sent.', { name, email: plan.create.contactEmail, declined })
        : t('programAdmin.confirm.suggestion.noteCreateNoEmail', 'A new person {name} is created without an email address and takes the spot, not invited yet. Add the address before inviting. {declined} comes off it; no email is sent.', { name, declined }),
    };
  }, [t]);

  // ---------------------------------------------------------------- spot actions

  // The picker of the fill and replace dialogs: search, and "Create a new person" (with the People
  // screen's duplicate check) when the owner hands in the two calls.
  const personField = useMemo(() => ({
    key: 'personId', type: 'person', required: true, search: searchPeople, create: createPerson, findMatches: findPersonMatches,
  }), [createPerson, findPersonMatches, searchPeople]);

  // An invitation needs the person's contact email (InvitationService refuses without one, the preview
  // too). Without it the invite dialog is preceded by this one: the address is saved on the person
  // (PersonAdminController edit, an ADMIN change; refused for an address another live person has),
  // then `then(email)` opens the invitation dialog for that address.
  const askForEmail = useCallback((personId, name, then) => openDialog({
    target: { kind: 'setContactEmail', personId },
    title: t('programAdmin.confirm.addEmail.title', '{name} has no email address yet', { name }),
    points: [
      t('programAdmin.confirm.addEmail.why', 'An invitation needs a contact email. Enter it here: it is saved on the person, then the invitation dialog opens with the email preview'),
      t('programAdmin.confirm.addEmail.unique', 'An address another person already has is refused'),
      t('programAdmin.confirm.addEmail.noMail', 'Nothing is sent yet: the invitation is its own step'),
    ],
    fields: [{
      key: 'contactEmail', type: 'email', required: true, value: '', placeholder: 'name@example.org',
      label: t('programAdmin.confirm.addEmail.field', 'Contact email'),
      validate: (value) => /^[^\s@]+@[^\s@]+$/.test((value || '').trim()),
    }],
    history: { kept: true, note: t('programAdmin.confirm.addEmail.history', 'The address is recorded on the person as an admin change.') },
    confirmLabel: t('programAdmin.confirm.addEmail.button', 'Save and continue to the invitation'),
    next: (values) => then((values.contactEmail || '').trim()),
  }), [openDialog, t]);

  const openSpotAction = useCallback((action, spot, entry, given = {}) => {
    const person = personById(people, spot.personId);
    const name = person?.programName || spot.placeholderLabel || t('programAdmin.tba', 'TBA');
    const label = spot.placeholderLabel || t('programAdmin.tba', 'TBA');
    const title = nodeTitle(entry, language);
    const here = `${title} (${roleText(spot.role)})`;
    const one = [{ personId: spot.personId, assignmentIds: [spot.id], labels: [here], name }];

    switch (action) {
      case 'invite': {
        // `given.contactEmail`: the address just saved by askForEmail (the people list is reloaded
        // meanwhile, this dialog is built from the one it was opened with).
        const to = given.contactEmail || person?.contactEmail;
        if (!to && spot.personId != null) {
          return askForEmail(spot.personId, name, (email) => openSpotAction('invite', spot, entry, { contactEmail: email }));
        }
        return openDialog({
          target: { kind: 'invite', people: one.map((p) => ({ ...p, to })) },
          title: t('programAdmin.confirm.invite.title', 'Send the invitation to {name}?', { name }),
          points: [
            t('programAdmin.confirm.invite.email', 'One invitation email to {name} at {to}, listing this spot: {entry}, {role}', { name, to, entry: title, role: roleText(spot.role) }),
            t('programAdmin.confirm.invite.state', 'The spot becomes Invited; {label} is printed until they answer', { label }),
            t('programAdmin.confirm.invite.other', 'Their other spots at this event are not asked again by this email'),
            t('programAdmin.confirm.invite.research', 'AI research does not need to be checked first: what the team has not decided goes to the person as proposed changes in their profile, also when it finishes later. If no research has run yet, it starts now.'),
          ],
          history: { kept: true, note: keepHistory },
          confirmLabel: t('programAdmin.confirm.invite.button', 'Send invitation'),
        });
      }
      case 'remind': {
        const open = openSpotsOf(spot.personId);
        const reminders = personReminders(person);
        return openDialog({
          target: { kind: 'remind', people: [{ personId: spot.personId, assignmentIds: open, labels: labelsOf(open), name }] },
          title: t('programAdmin.confirm.remind.title', 'Send a reminder to {name}?', { name }),
          points: [
            t('programAdmin.confirm.remind.perPerson', 'One email to {name} at {to}. Reminders are per person: it covers all {count, plural, one {# open invitation} other {# open invitations}} of theirs at this event, not only this spot', { name, to: person?.contactEmail, count: open.length }),
            t('programAdmin.confirm.remind.count', 'Counts as a reminder ({sent} of {max} sent). Manual reminders are not refused, but after {max} automatic ones no further automatic reminder goes out', { sent: reminders.remindersSent || 0, max: reminders.maxReminders }),
            t('programAdmin.confirm.remind.list', 'The email lists every open invitation of theirs, with a fresh link for each'),
          ],
          history: { kept: true, note: t('programAdmin.confirm.remind.note', 'Every reminder is recorded on the invitation, with the date it went out.') },
          confirmLabel: t('programAdmin.confirm.remind.button', 'Send reminder'),
        });
      }
      case 'uninvite':
        return openDialog({
          target: { kind: 'uninvite', assignmentId: spot.id, people: one },
          tone: 'danger',
          title: t('programAdmin.confirm.uninvite.title', 'Uninvite {name}?', { name }),
          points: [
            t('programAdmin.confirm.uninvite.state', 'Close the open invitation as cancelled; the spot becomes an open spot and prints {label}', { label }),
            t('programAdmin.confirm.uninvite.link', 'The personal invitation link stops working'),
            t('programAdmin.confirm.uninvite.reminders', 'Reminders for this invitation stop - it is no longer open'),
            t('programAdmin.confirm.uninvite.history', 'The invitation stays in the spot\'s history'),
          ],
          history: { kept: true, note: keepHistory },
          confirmLabel: t('programAdmin.confirm.uninvite.button', 'Uninvite'),
        });
      case 'markDeclined':
        return openDialog({
          target: { kind: 'decline', assignmentId: spot.id },
          tone: 'danger',
          title: t('programAdmin.confirm.decline.title', 'Record that {name} declined?', { name }),
          points: [
            t('programAdmin.confirm.decline.state', 'Close the open invitation as declined; the spot becomes an open spot'),
            t('programAdmin.confirm.decline.reminders', 'Reminders for this invitation stop - it is no longer open'),
            t('programAdmin.confirm.decline.noMail', 'Nothing is sent to anybody: you are recording an answer that reached you another way'),
          ],
          fields: [
            { key: 'replacementName', label: t('programAdmin.confirm.decline.name', 'Suggested replacement – name (optional)'), placeholder: t('programAdmin.confirm.decline.namePlaceholder', 'e.g. Dr. Ada Example'), value: '' },
            { key: 'replacementEmail', label: t('programAdmin.confirm.decline.email', 'Suggested replacement – email (optional)'), type: 'email', placeholder: 'name@example.org', value: '' },
          ],
          history: { kept: true, note: t('programAdmin.confirm.decline.history', 'What they answered - and the replacement they named - is stored on the invitation that was declined.') },
          confirmLabel: t('programAdmin.confirm.decline.button', 'Record the decline'),
        });
      case 'withdraw':
        return openDialog({
          target: { kind: 'withdraw', assignmentId: spot.id, people: one },
          tone: 'danger',
          title: t('programAdmin.confirm.withdraw.title', 'Withdraw {name}?', { name }),
          points: [
            t('programAdmin.confirm.withdraw.state', 'Move this spot from Confirmed to Withdrawn; it becomes an open spot and prints {label}', { label }),
            t('programAdmin.confirm.withdraw.record', 'The withdrawal is recorded on the confirmed invitation it reverses'),
            t('programAdmin.confirm.withdraw.materials', 'If published slides of this talk relied on this person\'s confirmation, they are unpublished'),
            t('programAdmin.confirm.withdraw.others', 'Their other spots at this event are not touched'),
          ],
          history: { kept: true, note: keepHistory },
          confirmLabel: t('programAdmin.confirm.withdraw.button', 'Withdraw'),
        });
      case 'showPublicly':
        return openDialog({
          target: {
            kind: 'setShowPublicly', assignmentId: spot.id, value: !spot.showPublicly,
            groupLabel: spot.groupLabel ?? null, displayNote: spot.displayNote ?? null, placeholderLabel: spot.placeholderLabel ?? null,
          },
          title: spot.showPublicly
            ? t('programAdmin.confirm.showPublicly.offTitle', 'Hide {name} until they confirm?', { name })
            : t('programAdmin.confirm.showPublicly.onTitle', 'Show {name} before they confirm?', { name }),
          points: [
            spot.showPublicly
              ? t('programAdmin.confirm.showPublicly.off', 'The public program prints {label} on this spot until they confirm', { label })
              : t('programAdmin.confirm.showPublicly.on', 'The public program prints their name on this spot now, while they are proposed or invited'),
            t('programAdmin.confirm.showPublicly.confirmed', 'Once they confirm, the name is shown either way'),
            noMail,
          ],
          confirmLabel: spot.showPublicly
            ? t('programAdmin.confirm.showPublicly.offButton', 'Hide the name')
            : t('programAdmin.confirm.showPublicly.onButton', 'Show the name'),
        });
      case 'replacePerson':
        return openDialog({
          target: { kind: 'replacePerson', assignmentId: spot.id, people: one },
          title: t('programAdmin.confirm.replace.title', 'Replace the person on this spot?'),
          fields: [{ ...personField, label: t('programAdmin.confirm.replace.pick', 'New person') }],
          note: spot.status === 'INVITED'
            ? t('programAdmin.confirm.replace.noteInvited', '{name} comes off the spot and the new person is not invited yet. Their open invitation is cancelled; the email below tells them.', { name })
            : t('programAdmin.confirm.replace.note', '{name} comes off the spot and the new person is not invited yet. No email is sent.', { name }),
          confirmLabel: t('programAdmin.confirm.replace.button', 'Replace person'),
        });
      case 'fillSpot':
        return openDialog({
          target: { kind: 'replacePerson', assignmentId: spot.id },
          title: t('programAdmin.confirm.fill.title', 'Put a person on this spot?'),
          fields: [{ ...personField, label: t('programAdmin.confirm.replace.pick', 'Person') }],
          note: t('programAdmin.confirm.fill.note', 'They are not invited yet and no email is sent. Invite them from the spot when you are ready.'),
          confirmLabel: t('programAdmin.confirm.fill.button', 'Put on this spot'),
        });
      case 'remove': {
        const asked = (spot.history || []).length > 0;
        return openDialog({
          target: { kind: 'removeSpot', assignmentId: spot.id, people: spot.status === 'INVITED' ? one : [] },
          tone: 'danger',
          title: t('programAdmin.confirm.removeSpot.title', 'Remove this spot?'),
          points: [
            ...(spot.status === 'INVITED'
              ? [t('programAdmin.confirm.removeSpot.uninvite', 'The open invitation is cancelled first, and this email tells {name}', { name })]
              : []),
            t('programAdmin.confirm.removeSpot.leaves', 'It leaves the program, the invitations, the reminders and the open-spot list'),
            asked
              ? t('programAdmin.confirm.removeSpot.removed', 'Somebody was asked, so the spot is kept as removed and its history stays readable')
              : t('programAdmin.confirm.removeSpot.deleted', 'Nobody was ever asked, so the spot is deleted for good'),
          ],
          history: asked ? { kept: true, note: keepHistory } : { kept: false, note: dropHistory },
          confirmLabel: t('programAdmin.confirm.removeSpot.button', 'Remove spot'),
        });
      }
      case 'move': {
        const targets = [...index.entries.values()]
          .filter(({ entry: other }) => other.id !== entry.id && !other.removedAt)
          .map(({ entry: other }) => ({ value: String(other.id), label: `${nodeTitle(other, language)} — ${entryPath(program, other, language)}` }));
        return openDialog({
          target: { kind: 'moveSpot', assignmentId: spot.id },
          title: t('programAdmin.confirm.moveSpot.title', 'Move this spot to another entry?'),
          points: [
            t('programAdmin.confirm.moveSpot.target', 'Pick another entry of this event: the spot keeps its role, label, group and note, and is put last on the target'),
            t('programAdmin.confirm.moveSpot.rule', 'Only a spot nobody was asked about yet can move - anything else is a new engagement: add a spot on the other entry and remove this one'),
            t('programAdmin.confirm.moveSpot.label', 'What it prints until somebody takes it ({label}) and the role it is in move with it', { label }),
            noMail,
          ],
          fields: [{ key: 'entryId', type: 'select', label: t('programAdmin.confirm.moveSpot.pick', 'Target entry'), options: [{ value: '', label: t('programAdmin.confirm.pick', 'Pick…') }, ...targets] }],
          history: { kept: true, note: t('programAdmin.confirm.moveSpot.history', 'Nobody was asked about this spot, so there is no history to lose.') },
          confirmLabel: t('programAdmin.confirm.moveSpot.button', 'Move the spot'),
        });
      }
      case 'useSuggestion': {
        // The suggestion the person named lives on their declined invitation. The backend matches it
        // against every live person (SuggestedPersonService: the email, then the name; similar names
        // are candidates); the dialog shows that plan and the admin chooses: the person found, one
        // of the candidates, or a new person (plan §7.7 item 8).
        const suggestion = declineSuggestionOf(spot) || {};
        return openDialog({
          target: { kind: 'useSuggestion', assignmentId: spot.id },
          title: t('programAdmin.confirm.suggestion.title', 'Put {name} on this spot?', { name: suggestion.name || suggestion.email }),
          note: t('programAdmin.confirm.suggestion.note', '{declined} comes off the spot. The person you choose is not invited yet; no email is sent.', { declined: spot.personName }),
          confirmLabel: t('programAdmin.confirm.suggestion.button', 'Put on this spot'),
          load: fetchSuggestion ? async () => suggestionDialogOf(await fetchSuggestion(spot.id), spot, spot.personName) : undefined,
        });
      }
      default:
        return undefined;
    }
  }, [askForEmail, dropHistory, fetchSuggestion, index, keepHistory, labelsOf, language, noMail, openDialog, openSpotsOf, people, personField, program, roleText, suggestionDialogOf, t]);

  // ---------------------------------------------------------------- entry actions

  // A new "up"/"down" index: the neighbour entry's own index among the live siblings, entries and
  // display elements together (the index ProgramService.move expects).
  const swap = useCallback((entry, direction) => {
    const parent = index.entries.get(entry.id)?.parent || null;
    const siblings = (parent ? parent.children || [] : program?.entries || [])
      .filter((n) => !n.removedAt && (parent || n.eventDayId === entry.eventDayId))
      .slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const entries = siblings.filter(isEntry);
    const at = entries.findIndex((e) => e.id === entry.id);
    const neighbour = entries[at + (direction === 'up' ? -1 : 1)];
    if (!neighbour) return null;
    // ProgramService.move inserts the entry at `position` among the siblings *without* it, so the
    // neighbour's index is taken in that list.
    const withoutMoved = siblings.filter((n) => n.id !== entry.id);
    return {
      parentId: parent ? parent.id : null,
      eventDayId: parent ? null : entry.eventDayId,
      roomId: parent ? null : entry.roomId,
      position: withoutMoved.findIndex((n) => n.id === neighbour.id),
    };
  }, [index, program]);

  const openEntryAction = useCallback((action, entry) => {
    const title = nodeTitle(entry, language);
    const where = entryPath(program, entry, language);
    switch (action) {
      case 'edit':
        return openDialog({
          target: { kind: 'editEntry', entryId: entry.id },
          title: t('programAdmin.confirm.edit.title', 'Edit {title}?', { title }),
          points: [
            t('programAdmin.confirm.edit.content', 'Title, subtitle, times, room and the internal note of this entry'),
            t('programAdmin.confirm.edit.visibility', 'Whether the entry and everything below it is public or internal'),
            notifyBullet,
          ],
          fields: [
            { key: 'kind', type: 'select', label: t('programAdmin.edit.kind', 'Kind'), value: entry.kind, options: kindOptions },
            { key: 'titleDe', label: t('programAdmin.edit.titleDe', 'Title (DE)'), value: entry.titleDe || '' },
            { key: 'titleEn', label: t('programAdmin.edit.titleEn', 'Title (EN)'), value: entry.titleEn || '' },
            { key: 'subtitleDe', label: t('programAdmin.edit.subtitleDe', 'Subtitle (DE)'), value: entry.subtitleDe || '' },
            { key: 'subtitleEn', label: t('programAdmin.edit.subtitleEn', 'Subtitle (EN)'), value: entry.subtitleEn || '' },
            { key: 'labelDe', label: t('programAdmin.edit.labelDe', 'Label (DE)'), value: entry.labelDe || '' },
            { key: 'labelEn', label: t('programAdmin.edit.labelEn', 'Label (EN)'), value: entry.labelEn || '' },
            { key: 'startTime', type: 'time', label: t('programAdmin.edit.startTime', 'Start'), value: entry.startTime || '' },
            { key: 'endTime', type: 'time', label: t('programAdmin.edit.endTime', 'End'), value: entry.endTime || '' },
            // only a top-level entry has a room (ProgramService.editEntry)
            ...(entry.depth === 0 ? [{ key: 'roomId', type: 'select', label: t('programAdmin.edit.room', 'Room'), value: entry.roomId != null ? String(entry.roomId) : '', options: roomOptionsOf() }] : []),
            { key: 'visibility', type: 'select', label: t('programAdmin.edit.visibility', 'Visibility'), value: entry.visibility, options: visibilityOptions },
            { key: 'asksForSlides', type: 'checkbox', label: t('programAdmin.edit.asksForSlides', 'Asks for slides'), value: !!entry.asksForSlides },
            { key: 'discussionMinutes', type: 'number', label: t('programAdmin.edit.discussion', 'Discussion minutes'), value: entry.discussionMinutes ?? '' },
            { key: 'seriesLabelDe', label: t('programAdmin.edit.seriesDe', 'Series label (DE)'), value: entry.seriesLabelDe || '' },
            { key: 'seriesLabelEn', label: t('programAdmin.edit.seriesEn', 'Series label (EN)'), value: entry.seriesLabelEn || '' },
            { key: 'focusDe', label: t('programAdmin.edit.focusDe', 'FOCUS text (DE)'), value: entry.focusDe || '' },
            { key: 'focusEn', label: t('programAdmin.edit.focusEn', 'FOCUS text (EN)'), value: entry.focusEn || '' },
            { key: 'internalNotes', label: t('programAdmin.edit.internalNotes', 'Internal note'), value: entry.internalNotes || '' },
          ],
          history: orderNote,
          confirmLabel: t('programAdmin.confirm.edit.button', 'Save the entry'),
        });
      case 'addChild':
        return openDialog({
          target: { kind: 'addChild', entryId: entry.id },
          title: t('programAdmin.confirm.addChild.title', 'Add an entry below {title}?', { title }),
          points: [
            t('programAdmin.confirm.addChild.depth', 'One level deeper than this entry ({where}); the tree is at most three levels deep', { where }),
            t('programAdmin.confirm.addChild.empty', 'It starts empty - no people; add a spot for each role. A start and end time are optional; without them it inherits its block\'s time'),
            noMail,
          ],
          fields: [
            { key: 'kind', type: 'select', label: t('programAdmin.edit.kind', 'Kind'), value: 'TALK', options: kindOptions },
            { key: 'titleDe', label: t('programAdmin.edit.titleDe', 'Title (DE)'), value: '' },
            { key: 'titleEn', label: t('programAdmin.edit.titleEn', 'Title (EN)'), value: '' },
            { key: 'startTime', type: 'time', label: t('programAdmin.edit.startTime', 'Start'), value: '' },
            { key: 'endTime', type: 'time', label: t('programAdmin.edit.endTime', 'End'), value: '' },
          ],
          history: orderNote,
          confirmLabel: t('programAdmin.confirm.addChild.button', 'Add child entry'),
        });
      case 'addSpot':
        return openDialog({
          target: { kind: 'addSpot', entryId: entry.id },
          title: t('programAdmin.confirm.addSpot.title', 'Add a spot to {title}?', { title }),
          points: [
            t('programAdmin.confirm.addSpot.state', 'A new spot in the role you pick, without a person and not invited yet'),
            t('programAdmin.confirm.addSpot.only', 'A new spot always starts like this: nobody on it and nobody invited'),
            noMail,
          ],
          fields: [
            { key: 'role', type: 'select', label: t('programAdmin.edit.role', 'Role'), value: 'SPEAKER', options: roleOptions },
            { key: 'placeholderLabel', label: t('programAdmin.edit.placeholder', 'Printed as (optional)'), value: '' },
          ],
          history: { kept: true, note: t('programAdmin.confirm.addSpot.note', 'Nobody has been asked yet, so the spot has no history.') },
          confirmLabel: t('programAdmin.confirm.addSpot.button', 'Add spot'),
        });
      case 'moveUp':
      case 'moveDown': {
        const siblings = siblingEntries(program, entry, index.entries.get(entry.id)?.parent);
        const at = positionOf(siblings, entry);
        const body = swap(entry, action === 'moveUp' ? 'up' : 'down');
        return openDialog({
          target: { kind: 'moveEntry', entryId: entry.id, body },
          title: action === 'moveUp'
            ? t('programAdmin.confirm.moveUp.title', 'Move {title} up?', { title })
            : t('programAdmin.confirm.moveDown.title', 'Move {title} down?', { title }),
          points: [
            t('programAdmin.confirm.move.order', 'Swap it with the entry next to it among its siblings ({where})', { where }),
            t('programAdmin.confirm.move.ends', 'There {count, plural, one {is # sibling} other {are # siblings}} - the first has nothing above it, the last nothing below', { count: at.count }),
            notifyBullet,
          ],
          history: orderNote,
          confirmLabel: action === 'moveUp'
            ? t('programAdmin.confirm.moveUp.button', 'Move up')
            : t('programAdmin.confirm.moveDown.button', 'Move down'),
        });
      }
      case 'moveTo': {
        // Not into itself or anything below it (ProgramService.move refuses that), and only where
        // the tree stays three levels deep.
        const below = new Set();
        const collect = (node) => (node.children || []).filter(isEntry).forEach((c) => { below.add(c.id); collect(c); });
        collect(entry);
        const parents = [...index.entries.values()]
          .filter(({ entry: other }) => other.id !== entry.id && !below.has(other.id) && !other.removedAt && other.depth < 2)
          .map(({ entry: other }) => ({ value: String(other.id), label: `${nodeTitle(other, language)} — ${entryPath(program, other, language)}` }));
        const currentParent = index.entries.get(entry.id)?.parent?.id ?? null;
        // A child entry has no day of its own; moving it to the top level needs one, so the picker
        // starts at its parent's day.
        const dayDefault = entry.eventDayId ?? index.entries.get(entry.id)?.parent?.eventDayId ?? days[0]?.id ?? null;
        const start = entry.startTime ? formatTime(entry.startTime) : null;
        return openDialog({
          target: { kind: 'moveEntry', entryId: entry.id },
          title: t('programAdmin.confirm.moveTo.title', 'Move {title} to another place?', { title }),
          points: [
            t('programAdmin.confirm.moveTo.what', 'Moves this entry, with everything below it and every person on it, to another place in the program. Right now it is in: {where}', { where }),
            t('programAdmin.confirm.moveTo.target', 'Choose where it goes: into another entry (for example a talk into another session), or "Top level of a day" with a day and a room'),
            start
              ? t('programAdmin.confirm.moveTo.timed', 'Where it stands in the list follows its start time ({time}). To change when it happens, use Edit instead', { time: start })
              : t('programAdmin.confirm.moveTo.untimed', 'It has no start time, so it goes to the end of its new place; use Up and Down there to put it in order'),
            t('programAdmin.confirm.moveTo.under', 'Inside another entry it has no day or room of its own, and its series label and FOCUS text are cleared'),
            t('programAdmin.confirm.moveTo.people', 'A confirmed person stays on it, and if confirmed or invited people are affected you choose next who is told'),
            t('programAdmin.confirm.moveTo.unchanged', 'The button stays off until you choose a place that differs from the current one'),
          ],
          fields: [
            { key: 'parentId', type: 'select', label: t('programAdmin.edit.parent', 'Into entry'), value: currentParent != null ? String(currentParent) : '', options: [{ value: '', label: t('programAdmin.edit.topLevel', 'Top level of a day') }, ...parents] },
            { key: 'eventDayId', type: 'select', label: t('programAdmin.edit.moveDay', 'Day (top level only)'), value: dayDefault != null ? String(dayDefault) : '', options: days.map((d) => ({ value: String(d.id), label: dayLabel(intl, language, d) })) },
            { key: 'roomId', type: 'select', label: t('programAdmin.edit.moveRoom', 'Room (top level only)'), value: entry.roomId != null ? String(entry.roomId) : '', options: roomOptionsOf() },
          ],
          // Off while the choice is where the entry already is: such a move would change nothing.
          blockConfirm: (fields) => {
            const value = (key) => fields.find((f) => f.key === key)?.value ?? '';
            const parent = value('parentId');
            if (parent !== '') return parent === String(currentParent ?? '');
            return currentParent == null
              && value('eventDayId') === String(entry.eventDayId ?? '')
              && value('roomId') === String(entry.roomId ?? '');
          },
          history: orderNote,
          confirmLabel: t('programAdmin.confirm.moveTo.button', 'Move it here'),
        });
      }
      case 'remove': {
        // An entry's removal owns its whole subtree (ProgramService.removeEntry / AssignmentService
        // .removeAllOf): the spot count, the "somebody was asked" check and the cancellation notices
        // all look at the spots below it, and a confirmed spot anywhere in it refuses the removal.
        const spots = subtreeSpots(entry);
        const asked = spots.some(({ assignment }) => (assignment.history || []).length > 0);
        const invited = invitedInSubtree(entry);
        const byPerson = new Map();
        invited.forEach(({ assignment, entry: spotEntry }) => {
          const person = personById(people, assignment.personId);
          if (!person) return;
          if (!byPerson.has(person.personId)) byPerson.set(person.personId, { personId: person.personId, name: person.programName, assignmentIds: [], labels: [] });
          const row = byPerson.get(person.personId);
          row.assignmentIds.push(assignment.id);
          row.labels.push(`${nodeTitle(spotEntry, language)} (${roleText(assignment.role)})`);
        });
        const target = { kind: 'removeEntry', entryId: entry.id, people: [...byPerson.values()] };
        return openDialog({
          target,
          tone: 'danger',
          title: t('programAdmin.confirm.removeEntry.title', 'Remove {title}?', { title }),
          points: [
            t('programAdmin.confirm.removeEntry.subtree', 'The entry and everything below it leaves the program: {count, plural, one {# spot} other {# spots}}, entry and descendants included', { count: spots.length }),
            asked
              ? t('programAdmin.confirm.removeEntry.removed', 'Somebody was asked, so the subtree is kept as removed and its history stays readable')
              : t('programAdmin.confirm.removeEntry.deleted', 'Nobody was ever asked about any of it, so the whole subtree is deleted for good'),
            invited.length > 0
              ? t('programAdmin.confirm.removeEntry.notices', 'The removal cancels {count, plural, one {# open invitation} other {# open invitations}} below it: one email per person, {people, plural, one {# person} other {# people}} in total', { count: invited.length, people: byPerson.size })
              : noMail,
          ],
          history: asked ? { kept: true, note: keepHistory } : { kept: false, note: dropHistory },
          // Besides today's cancellation preview, ask what closing the gap would move. A refused
          // plan leaves the dialog as it was; the DELETE reports the refusal with the backend's
          // words, as today.
          load: previewChange ? async () => {
            let preview;
            try {
              preview = await previewChange(target, { times: { closeGap: false } }, []);
            } catch (err) {
              return {};
            }
            const gap = preview?.gapClosing || [];
            const refusal = preview?.shiftRefusal || null;
            if (!refusal && gap.length === 0) return {};
            const firstPulled = gap.find((row) => row.reason === 'PULLED');
            const gapMinutes = firstPulled
              ? Math.abs(minutesOfClock(firstPulled.fromStart) - minutesOfClock(firstPulled.toStart)) : 0;
            return {
              timesPreview: preview,
              timesRefusal: refusal,
              fields: [{
                key: 'closeGap', type: 'checkbox', value: false, disabled: !!refusal,
                label: refusal || t('programAdmin.confirm.times.closeGap',
                  'Close the gap: move the following {count, plural, one {# program point} other {# program points}} {minutes} minutes earlier',
                  { count: gap.length, minutes: gapMinutes }),
              }],
              timeRows: shiftRowsOf(gap),
              timeRowsTitle: null,
            };
          } : undefined,
          confirmLabel: t('programAdmin.confirm.removeEntry.button', 'Remove entry'),
        });
      }
      default:
        return undefined;
    }
  }, [days, dropHistory, index, intl, keepHistory, kindOptions, language, noMail, notifyBullet, openDialog, orderNote, people, previewChange, program, roleOptions, roleText, roomOptionsOf, shiftRowsOf, swap, t, visibilityOptions]);

  // ---------------------------------------------------------------- the callbacks the screen passes down

  const spotActions = useMemo(() => ({
    inviteSpot: (spot, entry) => openSpotAction('invite', spot, entry),
    remindSpot: (spot, entry) => openSpotAction('remind', spot, entry),
    uninviteSpot: (spot, entry) => openSpotAction('uninvite', spot, entry),
    markDeclined: (spot, entry) => openSpotAction('markDeclined', spot, entry),
    withdrawSpot: (spot, entry) => openSpotAction('withdraw', spot, entry),
    replacePerson: (spot, entry) => openSpotAction('replacePerson', spot, entry),
    removeSpot: (spot, entry) => openSpotAction('remove', spot, entry),
    moveSpot: (spot, entry) => openSpotAction('move', spot, entry),
    toggleShowPublicly: (spot, entry) => openSpotAction('showPublicly', spot, entry),
    useSuggestion: (spot, entry) => openSpotAction('useSuggestion', spot, entry),
    // A spot nobody is on yet is filled from the panel, where the person is chosen.
    fillSpot: (spot, entry) => openSpotAction('fillSpot', spot, entry),
  }), [openSpotAction]);

  // ---------------------------------------------------------------- display elements

  // A text, quote or heading among the entries (ProgramController's display-element routes). No
  // person is affected by one, so its dialogs only describe and save: no email, no change route, no
  // times. The texts are multi-line; at least one of the two is needed (ProgramService.applyElement),
  // so the button stays off while both are blank.
  const elementKindOptions = useMemo(() => DISPLAY_KINDS.map((kind) => ({
    value: kind, label: t(`programAdmin.element.kind.${kind}`, DISPLAY_KIND_NAMES[kind]),
  })), [t]);
  const elementFieldsOf = useCallback((element) => [
    { key: 'kind', type: 'select', label: t('programAdmin.edit.kind', 'Kind'), value: element?.kind || 'TEXT', options: elementKindOptions },
    { key: 'textDe', type: 'textarea', label: t('programAdmin.edit.textDe', 'Text (DE)'), value: element?.textDe || '' },
    { key: 'textEn', type: 'textarea', label: t('programAdmin.edit.textEn', 'Text (EN)'), value: element?.textEn || '' },
    { key: 'highlighted', type: 'checkbox', label: t('programAdmin.edit.highlighted', 'Highlighted box'), value: element?.style === HIGHLIGHTED_BOX },
  ], [elementKindOptions, t]);
  const withoutText = useCallback((fields) => ['textDe', 'textEn']
    .every((key) => String(fields.find((f) => f.key === key)?.value ?? '').trim() === ''), []);
  const oneText = useMemo(() => t('programAdmin.confirm.element.oneText',
    'At least one of the two texts is needed; a page in the other language prints the one there is'), [t]);

  // Where a new element can go: the start, right after one of the live siblings (entries and
  // elements, in the order the backend counts them - placeSiblings), or the end (the default,
  // sent as no position).
  const placeOptionsOf = useCallback((parent, dayId) => {
    const siblings = placeSiblings(program, parent, dayId);
    const labelOf = (node) => {
      if (isEntry(node)) return [node.startTime ? formatTime(node.startTime) : null, nodeTitle(node, language)].filter(Boolean).join(' ');
      const text = pick(language, node.textDe, node.textEn).replace(/\s+/g, ' ').trim();
      return `“${text.length > 40 ? `${text.slice(0, 40)}…` : text}”`;
    };
    return [
      ...(siblings.length > 0 ? [{ value: '0', label: t('programAdmin.place.start', 'At the start') }] : []),
      ...siblings.slice(0, -1).map((node, i) => ({
        value: String(i + 1), label: t('programAdmin.place.after', 'After {label}', { label: labelOf(node) }),
      })),
      { value: '', label: t('programAdmin.place.end', 'At the end') },
    ];
  }, [language, program, t]);

  const openElementAction = useCallback((action, element) => {
    switch (action) {
      case 'edit':
        return openDialog({
          target: { kind: 'editElement', elementId: element.id, style: element.style ?? null },
          title: t('programAdmin.confirm.editElement.title', 'Edit this text?'),
          points: [
            t('programAdmin.confirm.element.what', 'Its kind (text, quote or heading), the German and the English text, and whether it stands in a highlighted box'),
            oneText,
            noMail,
          ],
          fields: elementFieldsOf(element),
          blockConfirm: withoutText,
          confirmLabel: t('programAdmin.confirm.editElement.button', 'Save the text'),
        });
      case 'remove':
        return openDialog({
          target: { kind: 'removeElement', elementId: element.id },
          tone: 'danger',
          title: t('programAdmin.confirm.removeElement.title', 'Delete this text?'),
          points: [
            t('programAdmin.confirm.removeElement.gone', 'It leaves the program for good: the list here and the public program'),
            t('programAdmin.confirm.removeElement.rest', 'The entries around it keep their place'),
            noMail,
          ],
          history: { kept: false, note: t('programAdmin.confirm.removeElement.history', 'A text has no history, so nothing of it is kept.') },
          confirmLabel: t('programAdmin.confirm.removeElement.button', 'Delete text'),
        });
      default:
        return undefined;
    }
  }, [elementFieldsOf, noMail, oneText, openDialog, t, withoutText]);

  // "Add text": inside an entry (its panel) or on the top level of the day on screen (below the day
  // tabs). The day is the one shown, so the places offered are that day's.
  const addElement = useCallback((parent, dayId) => {
    const title = parent ? nodeTitle(parent, language) : null;
    const day = (program?.days || []).find((d) => d.id === dayId);
    const dayText = day ? dayLabel(intl, language, day) : '';
    return openDialog({
      target: parent ? { kind: 'addElement', parentEntryId: parent.id } : { kind: 'addElement', parentEntryId: null, eventDayId: dayId },
      title: parent
        ? t('programAdmin.confirm.addElement.titleInside', 'Add a text inside {title}?', { title })
        : t('programAdmin.confirm.addElement.titleDay', 'Add a text to {day}?', { day: dayText }),
      points: [
        parent
          ? t('programAdmin.confirm.addElement.whereInside', 'It stands among the entries below {title}, at the place you pick', { title })
          : t('programAdmin.confirm.addElement.whereDay', 'It stands on the top level of {day}, at the place you pick: at the start of the day or right after the entry it follows', { day: dayText }),
        oneText,
        noMail,
      ],
      fields: [
        { key: 'position', type: 'select', label: t('programAdmin.edit.place', 'Place'), value: '', options: placeOptionsOf(parent, dayId) },
        ...elementFieldsOf(null),
      ],
      blockConfirm: withoutText,
      confirmLabel: t('programAdmin.confirm.addElement.button', 'Add text'),
    });
  }, [elementFieldsOf, intl, language, noMail, oneText, openDialog, placeOptionsOf, program, t, withoutText]);

  const elementActions = useMemo(() => ({
    editElement: (element) => openElementAction('edit', element),
    removeElement: (element) => openElementAction('remove', element),
  }), [openElementAction]);

  const entryActions = useMemo(() => ({
    editEntry: (entry) => openEntryAction('edit', entry),
    addChild: (entry) => openEntryAction('addChild', entry),
    addText: (entry) => addElement(entry, null),
    addSpot: (entry) => openEntryAction('addSpot', entry),
    moveUp: (entry) => openEntryAction('moveUp', entry),
    moveDown: (entry) => openEntryAction('moveDown', entry),
    moveTo: (entry) => openEntryAction('moveTo', entry),
    removeEntry: (entry) => openEntryAction('remove', entry),
  }), [addElement, openEntryAction]);

  // "Add day" / "Edit day": ProgramRequests.Day. A new day sends sortOrder 0, as the import does (the
  // backend orders days by sortOrder first and then the date, so 0 keeps the date order); an edit keeps
  // the day's own sortOrder. A duplicate date is the backend's 409, shown in the dialog.
  const openDayAction = useCallback((day = null) => openDialog({
    target: day
      ? { kind: 'updateDay', dayId: day.id, sortOrder: day.sortOrder }
      : { kind: 'createDay', sortOrder: 0 },
    title: day ? t('programAdmin.day.edit.title', 'Edit the day') : t('programAdmin.day.create.title', 'Add a day'),
    note: day
      ? t('programAdmin.day.edit.note', 'The date and the optional labels of this day.')
      : t('programAdmin.day.create.note', "A day of the event's program, in date order. Blocks and texts are added on it afterwards."),
    points: [noMail],
    fields: [
      { key: 'day', type: 'date', label: t('programAdmin.day.date', 'Date'), value: day?.day || defaultDayDate(program, days), required: true },
      { key: 'labelDe', label: t('programAdmin.day.labelDe', 'Label (DE)'), value: day?.labelDe || '' },
      { key: 'labelEn', label: t('programAdmin.day.labelEn', 'Label (EN)'), value: day?.labelEn || '' },
    ],
    confirmLabel: day ? t('programAdmin.day.edit.button', 'Save day') : t('programAdmin.day.create.button', 'Add day'),
  }), [days, noMail, openDialog, program, t]);

  // "Remove day": the backend refuses (409) a day that still has entries or display elements; the
  // dialog shows that sentence and stays open.
  const openRemoveDay = useCallback((day) => openDialog({
    target: { kind: 'deleteDay', dayId: day.id },
    tone: 'danger',
    title: t('programAdmin.day.remove.title', 'Remove the day?'),
    note: t('programAdmin.day.remove.note',
      'An empty day is removed. A day that still has entries or display elements stays: the server refuses it with a message.'),
    points: [noMail],
    confirmLabel: t('programAdmin.day.remove.button', 'Remove day'),
  }), [noMail, openDialog, t]);

  // The room dialog's fields, once the venue list is read: the room's own venue on edit, else the
  // preselect rule (venuePreselectOf). "New venue…" adds the two fields a new venue needs, in
  // changeField. At least one name is checked by blockConfirm, next to the confirm button.
  const roomFieldsOf = useCallback((room, venues) => {
    const list = venues || [];
    const selected = room ? room.venueId : venuePreselectOf(program, list);
    return [
      { key: 'nameDe', label: t('programAdmin.rooms.nameDe', 'Name (DE)'), value: room?.nameDe || '' },
      { key: 'nameEn', label: t('programAdmin.rooms.nameEn', 'Name (EN)'), value: room?.nameEn || '',
        hint: t('programAdmin.rooms.nameHint', 'A room needs a name in at least one language.') },
      {
        key: 'venueId', type: 'select', label: t('programAdmin.rooms.venue', 'Venue'),
        value: selected != null ? String(selected) : '',
        options: [
          { value: '', label: t('programAdmin.rooms.chooseVenue', 'Choose a venue…') },
          ...list.map((venue) => ({ value: String(venue.id), label: venueText(venue) })),
          { value: 'NEW', label: t('programAdmin.rooms.newVenue', 'New venue…') },
        ],
      },
    ];
  }, [program, t]);

  const roomBlockConfirm = useCallback((fields) => {
    const valueOf = (key) => String((fields || []).find((f) => f.key === key)?.value ?? '').trim();
    const nameMissing = !valueOf('nameDe') && !valueOf('nameEn');
    const venue = valueOf('venueId');
    return nameMissing || !venue || (venue === 'NEW' && !valueOf('newVenueName'));
  }, []);

  // "Add room" / "Edit room": the venue list is read when the dialog opens (all venues, not only the
  // ones the event already uses). The write is runAction's: a new venue is created first, then the
  // room with its id.
  const openRoomAction = useCallback((room = null) => openDialog({
    target: { kind: 'saveRoom', roomId: room?.id ?? null, room },
    title: room ? t('programAdmin.rooms.edit.title', 'Edit the room')
      : t('programAdmin.rooms.create.title', 'Add a room'),
    note: t('programAdmin.rooms.note', "A room belongs to this event's program; the venue is shared by all events."),
    points: [noMail],
    fields: [],
    blockConfirm: roomBlockConfirm,
    confirmLabel: room ? t('programAdmin.rooms.edit.button', 'Save room')
      : t('programAdmin.rooms.create.button', 'Add room'),
    load: async () => {
      const venues = fetchVenues ? (await fetchVenues()) || [] : (program?.venues || []);
      return { fields: roomFieldsOf(room, venues) };
    },
  }), [fetchVenues, noMail, openDialog, program, roomBlockConfirm, roomFieldsOf, t]);

  // "Remove room": a 409 (still has entries) is the dialog's message.
  const openRemoveRoom = useCallback((room) => openDialog({
    target: { kind: 'deleteRoom', roomId: room.id },
    tone: 'danger',
    title: t('programAdmin.rooms.remove.title', 'Remove the room?'),
    note: t('programAdmin.rooms.remove.note',
      'The room is removed. A room that still has entries stays: the server refuses it with a message.'),
    points: [noMail],
    confirmLabel: t('programAdmin.rooms.remove.button', 'Remove room'),
  }), [noMail, openDialog, t]);

  // "Add block": a new top-level entry (ProgramService.createEntry with no parent). Every day of the
  // event is offered, an empty one included, in date order (the admin's daysOf), starting at the day
  // on screen.
  const addBlock = useCallback((dayId) => openDialog({
    target: { kind: 'addBlock' },
    title: t('programAdmin.confirm.addBlock.title', 'Add a block to the program?'),
    points: [
      t('programAdmin.confirm.addBlock.top', 'A new block on the top level of the day you pick, with its own times and room'),
      t('programAdmin.confirm.addBlock.empty', 'It starts empty: no people - add child entries and a spot for each role'),
      noMail,
    ],
    fields: [
      { key: 'eventDayId', type: 'select', label: t('programAdmin.edit.day', 'Day'), value: dayId != null ? String(dayId) : String(days[0]?.id ?? ''), options: days.map((d) => ({ value: String(d.id), label: dayLabel(intl, language, d) })) },
      { key: 'kind', type: 'select', label: t('programAdmin.edit.kind', 'Kind'), value: 'SESSION', options: kindOptions },
      { key: 'titleDe', label: t('programAdmin.edit.titleDe', 'Title (DE)'), value: '' },
      { key: 'titleEn', label: t('programAdmin.edit.titleEn', 'Title (EN)'), value: '' },
      { key: 'startTime', type: 'time', label: t('programAdmin.edit.startTime', 'Start'), value: '' },
      { key: 'endTime', type: 'time', label: t('programAdmin.edit.endTime', 'End'), value: '' },
      { key: 'roomId', type: 'select', label: t('programAdmin.edit.room', 'Room'), value: '', options: roomOptionsOf() },
    ],
    history: orderNote,
    confirmLabel: t('programAdmin.confirm.addBlock.button', 'Add block'),
  }), [days, intl, kindOptions, language, noMail, openDialog, orderNote, roomOptionsOf, t]);

  // "Invite selected": one invitation email per person, covering every ticked spot of theirs
  // (InvitationController takes one person and their assignmentIds). As for a single invitation, a
  // person without a contact email is asked for one first (askForEmail, one person after the other);
  // `given` holds the addresses saved on the way (personId -> address; the people list is reloaded
  // meanwhile, these dialogs are built from the one the action started with). The invitation dialog,
  // with its previews, opens once everybody has an address: the backend refuses the preview of a
  // person without one, which used to fail the whole dialog.
  const inviteSelected = useCallback((ids, given = {}) => {
    const byPerson = new Map();
    ids.forEach((id) => {
      const found = index.spots.get(id);
      if (!found) return;
      const person = personById(people, found.assignment.personId);
      if (!person) return;
      if (!byPerson.has(person.personId)) {
        byPerson.set(person.personId, {
          personId: person.personId, name: person.programName, to: given[person.personId] || person.contactEmail || '',
          assignmentIds: [], labels: [],
        });
      }
      const row = byPerson.get(person.personId);
      row.assignmentIds.push(found.assignment.id);
      row.labels.push(`${nodeTitle(found.entry, language)} (${roleText(found.assignment.role)})`);
    });
    const grouped = [...byPerson.values()];
    const withoutEmail = grouped.find((row) => !row.to);
    if (withoutEmail) {
      return askForEmail(withoutEmail.personId, withoutEmail.name,
        (email) => inviteSelected(ids, { ...given, [withoutEmail.personId]: email }));
    }
    return openDialog({
      target: { kind: 'invite', people: grouped },
      title: t('programAdmin.confirm.inviteSelected.title', 'Send {count, plural, one {# invitation} other {# invitations}}?', { count: ids.length }),
      points: [
        t('programAdmin.confirm.inviteSelected.grouped', 'One email per person, covering every selected spot of theirs:'),
        ...grouped.map((row) => t('programAdmin.confirm.inviteSelected.person', '{name} — {count, plural, one {# spot} other {# spots}}', { name: row.name, count: row.assignmentIds.length })),
        t('programAdmin.confirm.inviteSelected.openSpots', 'A spot without a person, a confirmed one and an invited one cannot be selected: the backend refuses those'),
        t('programAdmin.confirm.invite.research', 'AI research does not need to be checked first: what the team has not decided goes to the person as proposed changes in their profile, also when it finishes later. If no research has run yet, it starts now.'),
      ],
      history: { kept: true, note: keepHistory },
      confirmLabel: t('programAdmin.confirm.inviteSelected.button', 'Invite {count}', { count: ids.length }),
    });
  }, [askForEmail, index, keepHistory, language, openDialog, people, roleText, t]);

  const toggleChecked = useCallback((id) => setChecked((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);

  // ---------------------------------------------------------------- the People area

  const invitePerson = useCallback((row, given = {}) => {
    // One email covering every spot of theirs an invitation may start from; without an address the
    // address comes first (askForEmail).
    const to = given.contactEmail || row.contactEmail;
    if (!to) return askForEmail(row.personId, row.programName, (email) => invitePerson(row, { contactEmail: email }));
    const starts = row.spots.filter((spot) => ['PROPOSED', 'DECLINED', 'WITHDRAWN', 'CANCELLED'].includes(spot.status));
    return openDialog({
      target: { kind: 'invite', people: [{ personId: row.personId, name: row.programName, to, assignmentIds: starts.map((s) => s.assignmentId), labels: starts.map((s) => `${nodeTitle(s.entry, language)} (${roleText(s.role)})`) }] },
      title: t('programAdmin.confirm.invitePerson.title', 'Invite {name}?', { name: row.programName }),
      points: [
        t('programAdmin.confirm.invitePerson.spots', 'One invitation email covering {count, plural, one {# spot} other {# spots}} at this event', { count: starts.length }),
        t('programAdmin.confirm.invitePerson.state', 'Those spots become Invited; the rest of their program is untouched'),
        t('programAdmin.confirm.invitePerson.other', 'A confirmed spot cannot be asked again'),
      ],
      history: { kept: true, note: keepHistory },
      confirmLabel: t('programAdmin.confirm.invite.button', 'Send invitation'),
    });
  }, [askForEmail, keepHistory, language, openDialog, roleText, t]);

  const remindPerson = useCallback((row) => {
    const open = openSpotsOf(row.personId);
    const reminders = personReminders(row);
    return openDialog({
      target: { kind: 'remind', people: [{ personId: row.personId, assignmentIds: open, labels: labelsOf(open), name: row.programName }] },
      title: t('programAdmin.confirm.remind.title', 'Send a reminder to {name}?', { name: row.programName }),
      points: [
        t('programAdmin.confirm.remind.perPerson', 'One email to {name} at {to}. Reminders are per person: it covers all {count, plural, one {# open invitation} other {# open invitations}} of theirs at this event, not only this spot', { name: row.programName, to: row.contactEmail, count: reminders.openInvitations }),
        t('programAdmin.confirm.remind.count', 'Counts as a reminder ({sent} of {max} sent). Manual reminders are not refused, but after {max} automatic ones no further automatic reminder goes out', { sent: reminders.remindersSent || 0, max: reminders.maxReminders }),
        t('programAdmin.confirm.remind.list', 'The email lists every open invitation of theirs, with a fresh link for each'),
      ],
      history: { kept: true, note: t('programAdmin.confirm.remind.note', 'Every reminder is recorded on the invitation, with the date it went out.') },
      confirmLabel: t('programAdmin.confirm.remind.button', 'Send reminder'),
    });
  }, [labelsOf, openDialog, openSpotsOf, t]);

  const stopReminders = useCallback((row) => {
    const reminders = personReminders(row);
    return openDialog({
      target: { kind: 'stopReminders', personId: row.personId },
      tone: 'danger',
      title: t('programAdmin.confirm.stopReminders.title', 'Stop the reminders for {name}?', { name: row.programName }),
      points: [
        t('programAdmin.confirm.stopReminders.all', 'Reminders stop on every open invitation of theirs at this event: {count, plural, one {# invitation} other {# invitations}}', { count: reminders.openInvitations }),
        t('programAdmin.confirm.stopReminders.keep', 'The spots stay as they are; to remind them again, resume the reminders first'),
        t('programAdmin.confirm.stopReminders.again', 'A later invitation for them starts without the stop'),
        noMail,
      ],
      history: { kept: true, note: t('programAdmin.confirm.stopReminders.note', 'Reminders already sent stay in the history.') },
      confirmLabel: t('programAdmin.confirm.stopReminders.button', 'Stop reminders'),
    });
  }, [noMail, openDialog, t]);

  const resumeReminders = useCallback((row) => {
    const reminders = personReminders(row);
    return openDialog({
      target: { kind: 'resumeReminders', personId: row.personId },
      title: t('programAdmin.confirm.resumeReminders.title', 'Resume the reminders for {name}?', { name: row.programName }),
      points: [
        t('programAdmin.confirm.resumeReminders.all', 'Reminders start again on every open invitation of theirs at this event: {count, plural, one {# invitation} other {# invitations}}', { count: reminders.openInvitations }),
        t('programAdmin.confirm.resumeReminders.wait', 'The automatic reminder follows the usual rhythm again (every {days} days, at most {max} in total)', { days: reminders.daysBetweenReminders, max: reminders.maxReminders }),
        noMail,
      ],
      history: { kept: true, note: t('programAdmin.confirm.stopReminders.note', 'Reminders already sent stay in the history.') },
      confirmLabel: t('programAdmin.confirm.resumeReminders.button', 'Resume reminders'),
    });
  }, [noMail, openDialog, t]);

  const peopleActions = useMemo(() => ({
    invite: invitePerson,
    remind: remindPerson,
    stopReminders,
    resumeReminders,
  }), [invitePerson, remindPerson, stopReminders, resumeReminders]);

  // ---------------------------------------------------------------- the layout

  const body = () => {
    if (!program) {
      if (loadError) {
        // A foreign ?event= is refused with 403 (authority.EventScope): the calm answer, with the
        // dropdown still there so another event can be picked. Every other failure is an error.
        if (loadError?.response?.status === 403) {
          return (
            <>
              <EventBar events={events} eventId={eventId} onSelectEvent={(id) => {
                setInternalEventId(id); setSelection(null); setChecked(new Set()); setDayId(null);
                onSelectEvent?.(id);
              }} />
              <p style={{ color: '#64748b' }}><FormattedMessage id="programAdmin.load.noAccess" defaultMessage="You have no access to this event." /></p>
            </>
          );
        }
        return (
          <div style={{ padding: '1rem', background: '#fee2e2', color: '#991b1b' }}>
            <p style={{ margin: '0 0 .6rem' }}>{errorText(loadError, t('programAdmin.load.failed', 'Could not load the program.'))}</p>
            <Button type="button" onClick={onRetry}><FormattedMessage id="programAdmin.load.retry" defaultMessage="Try again" /></Button>
          </div>
        );
      }
      if (events.length === 0 && !loading) {
        return <p style={{ color: '#64748b' }}><FormattedMessage id="programAdmin.load.noEvents" defaultMessage="You have no program to work on yet." /></p>;
      }
      return <p style={{ color: '#64748b' }}><FormattedMessage id="programAdmin.load.loading" defaultMessage="Loading the program…" /></p>;
    }
    return (
      <>
        {loadError && (
          <div style={{ padding: '.7rem .85rem', marginBottom: '1rem', background: '#fee2e2', color: '#991b1b' }}>
            {errorText(loadError, t('programAdmin.load.failed', 'Could not load the program.'))}
          </div>
        )}
        <EventBar events={events} eventId={eventId} program={program}
          onSelectEvent={(id) => {
            setInternalEventId(id); setSelection(null); setChecked(new Set()); setDayId(null);
            onSelectEvent?.(id);
          }}
          onPreviewPublic={onPreviewPublic} />

        <AreaTabs
          areas={AREAS
            .filter((a) => a.key !== 'editors' || can(PERMISSIONS.programEditorsManage))
            .map((a) => ({ key: a.key, label: t(a.id, a.def) }))}
          active={area}
          onSelect={(key) => { setArea(key); setSelection(null); }}
        />

        {area === 'program' && (
          <ProgramArea
            eventId={eventId}
            program={program}
            language={language}
            people={people}
            selection={activeSelection}
            dayId={currentDayId}
            checked={checked}
            spotFilter={spotFilter}
            showRemoved={showRemoved}
            filterCounts={filterCounts}
            entryPosition={entryPosition}
            onSelectDay={setDayId}
            onToggleChecked={toggleChecked}
            onSelectFilter={setSpotFilter}
            onToggleShowRemoved={setShowRemoved}
            onSelectEntry={(entry) => setSelection({ kind: 'entry', id: entry.id })}
            onSelectSpot={(spot) => setSelection({ kind: 'spot', id: spot.id })}
            onSelectElement={(element) => setSelection({ kind: 'element', id: element.id })}
            onClosePanel={() => setSelection(null)}
            onOpenPerson={(personId) => setSelection({ kind: 'person', id: personId })}
            onInviteSelected={inviteSelected}
            onClearSelection={() => setChecked(new Set())}
            spotActions={spotActions}
            entryActions={entryActions}
            elementActions={elementActions}
            onAddBlock={addBlock}
            onAddText={(dayId) => addElement(null, dayId)}
            onAddDay={() => openDayAction()}
            onEditDay={openDayAction}
            onRemoveDay={openRemoveDay}
            onImportPdf={() => setArea('imports')}
            onAddRoom={() => openRoomAction()}
            onEditRoom={openRoomAction}
            onRemoveRoom={openRemoveRoom}
            onChanged={onReload}
            onDownloadMaterials={onDownloadMaterials}
          />
        )}

        {area === 'people' && (
          <>
            <PeopleArea rows={rows} program={program} language={language} actions={peopleActions} />
            {/* The one invite-reminder template lives on the E-Mails screen now (P2b); this is the
                way into it, deep-linked straight to the editor. The E-Mails screen is staff-only. */}
            {can(PERMISSIONS.staffArea) && (
              <p style={{ margin: '.25rem 0 1rem', fontSize: '.85rem' }}>
                <Link to="/admin/emails?tab=emails&key=SPEAKER_INVITE_REMINDER" style={{ color: '#0369a1', fontWeight: 700 }}>
                  <FormattedMessage id="programAdmin.reminderTemplate.link" defaultMessage="Edit the reminder email" />
                </Link>
              </p>
            )}
          </>
        )}

        {area === 'imports' && <ImportsArea eventId={eventId} onChanged={onReload} />}

        {area === 'organizers' && <OrganizersArea eventId={eventId} />}

        {area === 'publish' && (
          <PublishArea eventId={eventId} program={program} onChanged={onReload} onPreviewPublic={onPreviewPublic} />
        )}

        {area === 'editors' && can(PERMISSIONS.programEditorsManage) && <EditorsArea eventId={eventId} />}
      </>
    );
  };

  const tellCount = (confirm?.emails || []).filter((email) => email.send).length;
  const tellStale = !!confirm?.shared
    && JSON.stringify([confirm.shared.subject, confirm.shared.html]) !== confirm.shared.previewedKey;
  const sharedTemplate = confirm?.stage === 'tell' && confirm.shared ? {
    templateKey: confirm.shared.templateKey,
    subject: confirm.shared.subject,
    html: confirm.shared.html,
    placeholders: confirm.shared.placeholders,
    problems: confirm.shared.problems,
    error: confirm.shared.error,
    onChangeSubject: (value) => setConfirm((c) => (c ? { ...c, shared: { ...c.shared, subject: value, error: null } } : c)),
    onChangeHtml: (value) => setConfirm((c) => (c ? { ...c, shared: { ...c.shared, html: value, error: null } } : c)),
  } : null;

  return (
    <Container>
      <BackLink to="/admin/dashboard">
        <FormattedMessage id="programAdmin.back" defaultMessage="Back to the dashboard" />
      </BackLink>
      <Header>
        <div>
          <Title><FormattedMessage id="programAdmin.title" defaultMessage="Program" /></Title>
          <Subtitle>
            <FormattedMessage id="programAdmin.subtitle"
              defaultMessage="The program of an event and the people on it: roles, states, invitations and reminders." />
          </Subtitle>
        </div>
      </Header>

      {status && <Message role="status">{status}</Message>}

      {body()}

      <ConfirmDialog
        open={!!confirm}
        tone={confirm?.tone}
        title={confirm?.title}
        intro={confirm?.intro}
        points={confirm?.points}
        note={confirm?.note}
        timeRows={confirm?.timeRows}
        timeRowsTitle={confirm?.timeRowsTitle}
        warnings={confirm?.warnings}
        warningsTitle={confirm?.warningsTitle}
        emails={confirm?.emails}
        fields={confirm?.fields}
        history={confirm?.history}
        confirmLabel={confirm?.stage === 'tell'
          ? t('programAdmin.confirm.tell.saveAndTell', '{count, plural, one {Save and tell # person} other {Save and tell # people}}', { count: tellCount })
          : confirm?.confirmLabel}
        sharedTemplate={sharedTemplate}
        secondaryAction={confirm?.secondaryAction}
        showCancel={confirm?.showCancel !== false}
        error={confirm?.error}
        busy={confirm?.busy}
        loading={confirm?.loading}
        // An invitation or reminder with every email unticked would do nothing: its button stays off.
        // In the tell step nobody ticked, a problem in the shared email or a draft not previewed yet
        // keeps it off too.
        confirmDisabled={!!confirm?.confirmDisabled || !!confirm?.blockConfirm?.(confirm.fields)
          || (['invite', 'remind'].includes(confirm?.target?.kind) && (confirm?.emails || []).length > 0
            && confirm.emails.every((email) => email.hasEmail !== false && !email.send))
          || (confirm?.stage === 'tell' && (tellCount === 0 || tellStale || (confirm.shared?.problems || []).length > 0))}
        onToggleSendEmail={toggleSendEmail}
        onChangeSubject={(key, subject) => setConfirm((c) => (c ? { ...c, emails: c.emails.map((m) => (m.key === key ? { ...m, subject } : m)) } : c))}
        onChangeBody={(key, html) => setConfirm((c) => (c ? { ...c, emails: c.emails.map((m) => (m.key === key ? { ...m, html } : m)) } : c))}
        onChangeTemplate={(key, patch) => setConfirm((c) => (c ? { ...c, emails: c.emails.map((m) => (m.key === key ? { ...m, template: { ...m.template, ...patch } } : m)) } : c))}
        onRefreshPreview={refreshEmailPreview}
        onChangeField={changeField}
        onPickPerson={(key, hit) => setConfirm((c) => (c ? { ...c, fields: c.fields.map((f) => (f.key === key ? { ...f, value: hit ? hit.id : '' } : f)) } : c))}
        onConfirm={confirmAction}
        onClose={closeDialog}
      />
    </Container>
  );
};

export default ProgramAdminScreen;
